/**
 * NiV Dashboard Firebase Service
 *
 * This service provides all Firebase operations for the NiV (SaaS Admin) dashboard.
 * Completely isolated from the main firebase.js file.
 *
 * Collections:
 * - niv_clinic_management: Management records for each clinic
 * - niv_tasks: NiV's personal tasks
 *
 * Access is controlled by Firestore rules via Firebase Custom Claims (nivAdmin: true).
 */

import { getApps } from "firebase/app";
import { pickLeadExtras } from "../pages/dashboard/niv/shared/nivDerived";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
  writeBatch,
  getCountFromServer,
  collectionGroup,
} from "firebase/firestore";
import { getEffectiveLimits, getPlanDefaults, diffFromPlan } from "./planLimits";
import { deriveClinicRole } from "../config/permissionsRegistry";
// The one food contract, shared with the seed import and the coach-facing form
import { normalizeFood } from "../utils/foodsNormalize.mjs";
import { getLeadAnalyticsSnapshot, trackPublicLead } from "./publicAnalytics";

// ============================================
// FIREBASE INSTANCE
// ============================================

// Use existing app instance (initialized in firebase.js)
const app = getApps()[0];
const db = getFirestore(app);

// ============================================
// CONSTANTS
// ============================================

export const NIV_COLLECTIONS = {
  CLINIC_MANAGEMENT: "niv_clinic_management",
  TASKS: "niv_tasks",
  CALL_REQUESTS: "niv_call_requests",
  PLANS: "niv_plans",
  ALERTS: "niv_alerts",
  SYSTEM_NOTIFICATIONS: "niv_system_notifications",
  DAILY_REPORTS: "niv_daily_reports",
  ERROR_LOGS: "niv_error_logs",
  REGISTRATION_WHITELIST: "niv_registration_whitelist",
  CHURN_RESOLUTIONS: "niv_churn_resolutions",
  PROSPECTS: "niv_prospects",
  CLINIC_CRM: "niv_clinic_crm",
  TELEMETRY_DAILY: "niv_telemetry_daily",
  TELEMETRY_DAILY_THERAPIST: "niv_telemetry_daily_therapist",
  SYSTEM_DAILY: "niv_system_daily",
  CLINIC_USAGE: "niv_clinic_usage",
  THERAPIST_ADDITIONS: "niv_therapist_additions",
  SETTINGS: "niv_settings",
  LENNY_REVIEWS: "niv_lenny_reviews",
};

// Prospect pipeline stages (unified CRM: lead -> registered clinic)
export const PROSPECT_STAGES = {
  LEAD: "lead",
  CONTACTED: "contacted",
  APPROVED: "approved",
  REGISTERED: "registered",
  LOST: "lost",
};

export const PROSPECT_STAGE_ORDER = ["lead", "contacted", "approved", "registered", "lost"];

export const PROSPECT_STAGE_LABELS = {
  lead: "ליד חדש",
  contacted: "נוצר קשר",
  approved: "אושר וממתין להרשמה",
  registered: "אושר להרשמה ונרשם",
  lost: "לא רלוונטי",
};

export const PROSPECT_STAGE_COLORS = {
  lead: "#f59e0b",
  contacted: "#3b82f6",
  approved: "#8b5cf6",
  registered: "#22c55e",
  lost: "#9ca3af",
};

export const PROSPECT_SOURCE_LABELS = {
  homepage_lead: "דף הבית",
  homepage_contact: "דף הבית – צור קשר",
  homepage_solutions: "דף הבית – פתרונות",
  get_started_page: "דף התחלה",
  signup: "הרשמה",
  dashboard: "דשבורד",
  trial_popup: "פופאפ ניסיון",
  manual: "נוסף ידנית",
  whitelist_import: "אישור הרשמה (ישן)",
};

// Call request statuses
// Staff-therapist addition alert statuses
export const THERAPIST_ADDITION_STATUSES = {
  PENDING: "pending",
  RESOLVED: "resolved",
};

export const CALL_REQUEST_STATUSES = {
  PENDING: "pending",
  CONTACTED: "contacted",
  SCHEDULED: "scheduled",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

export const CALL_REQUEST_STATUS_LABELS = {
  pending: "ממתין",
  contacted: "יצרנו קשר",
  scheduled: "תואם",
  completed: "הושלם",
  cancelled: "בוטל",
};

export const CALL_REQUEST_STATUS_COLORS = {
  pending: "#f59e0b",
  contacted: "#3b82f6",
  scheduled: "#8b5cf6",
  completed: "#22c55e",
  cancelled: "#9ca3af",
};

// ============================================
// SYSTEM PLANS - HARD-CODED (Cannot be deleted)
// ============================================

/**
 * System Plans Configuration
 * These are the official plans with full details.
 * Base plans (trial, starter, starter-plus, premium) cannot be deleted.
 * Pilot plan (custom1) — free pilot program, identical capabilities to Premium.
 * Custom plans (custom2–custom10) are reserved for future custom groups.
 */

const PREMIUM_PLAN_LIMITS = {
  smsPerMonth: 1100,
  aiActionsPerMonth: 1100,
  maxPatients: 1100,
  maxRooms: 1100,
  maxTherapists: 1100,
};

const PREMIUM_PLAN_FEATURES = {
  canCreateRooms: true,
  canAddTherapists: true,
  canIntegrateMorning: true,
  canIntegrateGrow: true,
  canUseLeadsWebhook: true,
  isSoloClinic: false,
};

const PREMIUM_FEATURES_LIST_HEBREW = [
  "הודעות SMS ללא הגבלה (עד 1100/חודש)",
  "פעולות AI ללא הגבלה (עד 1100/חודש)",
  "מטופלים ללא הגבלה",
  "חדרי טיפול ללא הגבלה",
  "מטפלים נוספים ללא הגבלה",
  "אינטגרציה לחשבונית ירוקה",
];

export const SYSTEM_PLANS = {
  trial: {
    code: "trial",
    name: "Trial",
    nameHebrew: "ניסיון",
    monthlyPrice: 0,
    isSystem: true,
    sortOrder: 0,
    limits: {
      smsPerMonth: 20,
      aiActionsPerMonth: 50,
      maxPatients: 15,
      maxRooms: 0,
      maxTherapists: 1,
      trialDays: 30,
      maxWriteOpsPerTrial: 3000,
    },
    features: {
      canCreateRooms: false,
      canAddTherapists: false,
      canIntegrateMorning: true,
      isSoloClinic: true,
    },
    featuresListHebrew: [
      "עד 20 הודעות SMS בחודש",
      "עד 50 פעולות AI בחודש",
      "עד 15 מטופלים",
      "מטפל יחיד (סולו)",
      "ללא חדרי טיפול",
      "אינטגרציה לחשבונית ירוקה",
    ],
  },
  starter: {
    code: "starter",
    name: "Starter",
    nameHebrew: "סטארטר",
    monthlyPrice: 39.9,
    isSystem: true,
    sortOrder: 1,
    limits: {
      smsPerMonth: 250,
      aiActionsPerMonth: 1100,
      maxPatients: 10,
      maxRooms: 0,
      maxTherapists: 1,
    },
    features: {
      canCreateRooms: false,
      canAddTherapists: false,
      canIntegrateMorning: true,
      isSoloClinic: true,
    },
    featuresListHebrew: [
      "עד 250 הודעות SMS בחודש",
      "פעולות AI ללא הגבלה",
      "עד 10 מטופלים",
      "מטפל יחיד (סולו)",
      "ללא חדרי טיפול",
      "אינטגרציה לחשבונית ירוקה",
    ],
  },
  "starter-plus": {
    code: "starter-plus",
    name: "Starter Plus",
    nameHebrew: "סטארטר פלוס",
    monthlyPrice: 49.9,
    isSystem: true,
    sortOrder: 2,
    limits: {
      smsPerMonth: 250,
      aiActionsPerMonth: 1100,
      maxPatients: 15,
      maxRooms: 0,
      maxTherapists: 1,
    },
    features: {
      canCreateRooms: false,
      canAddTherapists: false,
      canIntegrateMorning: true,
      isSoloClinic: true,
    },
    featuresListHebrew: [
      "עד 250 הודעות SMS בחודש",
      "פעולות AI ללא הגבלה",
      "עד 15 מטופלים",
      "מטפל יחיד (סולו)",
      "ללא חדרי טיפול",
      "אינטגרציה לחשבונית ירוקה",
    ],
  },
  premium: {
    code: "premium",
    name: "Premium",
    nameHebrew: "פרימיום",
    monthlyPrice: 89.9,
    isSystem: true,
    sortOrder: 3,
    limits: { ...PREMIUM_PLAN_LIMITS },
    features: { ...PREMIUM_PLAN_FEATURES },
    featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW],
  },
  // Pilot plan — free pilot program, identical capabilities to Premium.
  // Kept under the "custom1" plan code for backward compatibility with existing Firestore records.
  custom1: { code: "custom1", name: "Pilot - Free", nameHebrew: "פיילוט - חינם", monthlyPrice: 0, isSystem: true, sortOrder: 4, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  // Custom plans — reserved for future custom groups (same capabilities as Premium)
  custom2: { code: "custom2", name: "Custom 2", nameHebrew: "מותאם 2", monthlyPrice: null, isSystem: true, sortOrder: 11, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom3: { code: "custom3", name: "Custom 3", nameHebrew: "מותאם 3", monthlyPrice: null, isSystem: true, sortOrder: 12, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom4: { code: "custom4", name: "Custom 4", nameHebrew: "מותאם 4", monthlyPrice: null, isSystem: true, sortOrder: 13, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom5: { code: "custom5", name: "Custom 5", nameHebrew: "מותאם 5", monthlyPrice: null, isSystem: true, sortOrder: 14, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom6: { code: "custom6", name: "Custom 6", nameHebrew: "מותאם 6", monthlyPrice: null, isSystem: true, sortOrder: 15, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom7: { code: "custom7", name: "Custom 7", nameHebrew: "מותאם 7", monthlyPrice: null, isSystem: true, sortOrder: 16, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom8: { code: "custom8", name: "Custom 8", nameHebrew: "מותאם 8", monthlyPrice: null, isSystem: true, sortOrder: 17, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom9: { code: "custom9", name: "Custom 9", nameHebrew: "מותאם 9", monthlyPrice: null, isSystem: true, sortOrder: 18, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
  custom10: { code: "custom10", name: "Custom 10", nameHebrew: "מותאם 10", monthlyPrice: null, isSystem: true, sortOrder: 19, limits: { ...PREMIUM_PLAN_LIMITS }, features: { ...PREMIUM_PLAN_FEATURES }, featuresListHebrew: [...PREMIUM_FEATURES_LIST_HEBREW] },
};

/**
 * Get all system plans as array
 */
export const getSystemPlansArray = () => {
  return Object.values(SYSTEM_PLANS).sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Get plan by code
 */
export const getPlanByCode = (code) => {
  return SYSTEM_PLANS[code] || SYSTEM_PLANS.trial;
};

/**
 * Get plan display name (Hebrew)
 */
export const getPlanDisplayName = (code) => {
  const plan = SYSTEM_PLANS[code];
  return plan ? plan.nameHebrew : "לא ידוע";
};

// Legacy - kept for compatibility
export const DEFAULT_PLANS = getSystemPlansArray();

export const CLINIC_STATUSES = {
  TRIAL: "trial",
  ACTIVE: "active",
  SUSPENDED: "suspended",
  CHURNED: "churned",
};

export const CLINIC_STATUS_LABELS = {
  [CLINIC_STATUSES.TRIAL]: "טרייל",
  [CLINIC_STATUSES.ACTIVE]: "פעיל",
  [CLINIC_STATUSES.SUSPENDED]: "מושהה",
  [CLINIC_STATUSES.CHURNED]: "עזב",
};

export const CLINIC_STATUS_COLORS = {
  [CLINIC_STATUSES.TRIAL]: "#f59e0b", // Yellow/Orange
  [CLINIC_STATUSES.ACTIVE]: "#22c55e", // Green
  [CLINIC_STATUSES.SUSPENDED]: "#ef4444", // Red
  [CLINIC_STATUSES.CHURNED]: "#6b7280", // Gray
};

export const PAYMENT_STATUSES = {
  PAID: "paid",
  UNPAID: "unpaid",
  LATE: "late",
};

export const PAYMENT_STATUS_LABELS = {
  [PAYMENT_STATUSES.PAID]: "שולם",
  [PAYMENT_STATUSES.UNPAID]: "לא שולם",
  [PAYMENT_STATUSES.LATE]: "באיחור",
};

export const PAYMENT_METHODS = {
  CASH: "cash",
  TRANSFER: "transfer",
  CREDIT: "credit",
  BIT: "bit",
  CHECK: "check",
};

export const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHODS.CASH]: "מזומן",
  [PAYMENT_METHODS.TRANSFER]: "העברה בנקאית",
  [PAYMENT_METHODS.CREDIT]: "אשראי",
  [PAYMENT_METHODS.BIT]: "אפליקציה",
  [PAYMENT_METHODS.CHECK]: "צ'ק",
};

export const TASK_STATUSES = {
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  DONE: "done",
};

export const TASK_STATUS_LABELS = {
  [TASK_STATUSES.TODO]: "לביצוע",
  [TASK_STATUSES.IN_PROGRESS]: "בתהליך",
  [TASK_STATUSES.DONE]: "הושלם",
};

export const TASK_PRIORITIES = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
};

export const TASK_PRIORITY_LABELS = {
  [TASK_PRIORITIES.LOW]: "נמוכה",
  [TASK_PRIORITIES.MEDIUM]: "בינונית",
  [TASK_PRIORITIES.HIGH]: "גבוהה",
};

export const TASK_PRIORITY_COLORS = {
  [TASK_PRIORITIES.LOW]: "#6b7280",
  [TASK_PRIORITIES.MEDIUM]: "#f59e0b",
  [TASK_PRIORITIES.HIGH]: "#ef4444",
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Serialize Firestore data for Redux (convert Timestamps to ISO strings)
 */
const serializeForRedux = (data) => {
  if (!data) return data;

  const serialized = { ...data };

  Object.keys(serialized).forEach((key) => {
    const value = serialized[key];

    if (value instanceof Timestamp) {
      serialized[key] = value.toDate().toISOString();
    } else if (
      value &&
      typeof value === "object" &&
      value.seconds !== undefined
    ) {
      // Handle Firestore Timestamp-like objects
      serialized[key] = new Date(value.seconds * 1000).toISOString();
    } else if (Array.isArray(value)) {
      serialized[key] = value.map((item) =>
        typeof item === "object" ? serializeForRedux(item) : item
      );
    }
  });

  return serialized;
};

/**
 * Generate unique ID
 */
const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current month string (YYYY-MM format)
 */
export const getCurrentMonthString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// ============================================
// NIV CLINIC MANAGEMENT - CRUD
// ============================================

/**
 * Create a new NiV clinic management record
 */
export const createNivClinicManagement = async (data) => {
  try {
    const collectionRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const newDocRef = doc(collectionRef);

    const now = serverTimestamp();

    const docData = {
      id: newDocRef.id,
      clinicRef: data.clinicRef,
      clinicName: data.clinicName || "",

      // Status
      status: data.status || CLINIC_STATUSES.TRIAL,
      statusHistory: [
        {
          status: data.status || CLINIC_STATUSES.TRIAL,
          changedAt: Timestamp.now(),
          note: "יצירה ראשונית",
        },
      ],

      // Payments
      monthlyPaymentAmount: data.monthlyPaymentAmount || null,
      paymentMethod: data.paymentMethod || null,
      paymentHistory: [],

      // Settings
      allowedSmsTokens: data.allowedSmsTokens || 20,

      // Contact
      primaryContactName: data.primaryContactName || null,
      primaryContactPhone: data.primaryContactPhone || null,
      primaryContactEmail: data.primaryContactEmail || null,
      referralSource: data.referralSource || null,

      // Dates
      contractRenewalDate: data.contractRenewalDate || null,
      lastContactDate: null,

      // Notes
      internalNotes: data.internalNotes || null,
      callLogs: [],

      createdAt: now,
      updatedAt: now,
    };

    await setDoc(newDocRef, docData);

    return { id: newDocRef.id, ...serializeForRedux(docData) };
  } catch (error) {
    console.error("[CK-9904] Error creating NiV clinic management:", {
      errorCode: "CK-9904",
      errorMessage: error.message,
      data,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single NiV clinic management record
 */
export const getNivClinicManagement = async (managementId) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT, managementId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: docSnap.id, ...docSnap.data() });
  } catch (error) {
    console.error("[CK-9903] Error fetching NiV clinic management:", {
      errorCode: "CK-9903",
      errorMessage: error.message,
      managementId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all NiV clinic management records with optional filters
 */
export const getAllNivClinics = async (filters = {}) => {
  try {
    const collectionRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    let q = query(collectionRef, orderBy("createdAt", "desc"));

    // Apply status filter
    if (filters.status) {
      q = query(
        collectionRef,
        where("status", "==", filters.status),
        orderBy("createdAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    
    // Get actual clinic data for plan info
    const clinicIds = [];
    const managementData = [];
    
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      managementData.push({ id: docSnap.id, ...data });
      if (data.clinicRef) {
        clinicIds.push(data.clinicRef);
      }
    });

    // Fetch actual clinic documents to get real plan info
    const clinicsMap = {};
    if (clinicIds.length > 0) {
      const clinicsRef = collection(db, "clinics");
      const clinicsSnap = await getDocs(clinicsRef);
      clinicsSnap.forEach((docSnap) => {
        if (clinicIds.includes(docSnap.id)) {
          clinicsMap[docSnap.id] = docSnap.data();
        }
      });
    }

    // Merge management data with actual clinic data
    const clinics = managementData.map((mgmt) => {
      const actualClinic = clinicsMap[mgmt.clinicRef] || {};
      const actualPlanCode = actualClinic.plan || "trial";
      const planDetails = SYSTEM_PLANS[actualPlanCode] || SYSTEM_PLANS.trial;

      return serializeForRedux({
        ...mgmt,
        // Actual clinic plan info (source of truth)
        actualPlanCode,
        actualPlanName: planDetails.nameHebrew,
        actualPlanPrice: planDetails.monthlyPrice,
        planLimits: planDetails.limits,
        planFeatures: planDetails.features,
        // Display-friendly plan name
        planName: mgmt.isCustomPayment 
          ? `${planDetails.nameHebrew} (מותאם)` 
          : planDetails.nameHebrew,
        // Keep the effective payment (custom or from plan)
        effectivePayment: mgmt.isCustomPayment 
          ? mgmt.monthlyPaymentAmount 
          : planDetails.monthlyPrice,
        // Actual clinic data
        actualClinicCreatedAt: actualClinic.createdAt,
        trialEndDate: actualClinic.trialEndDate,
      });
    });

    // Client-side search filter (for clinic name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return clinics.filter(
        (clinic) =>
          clinic.clinicName?.toLowerCase().includes(searchLower) ||
          clinic.primaryContactName?.toLowerCase().includes(searchLower)
      );
    }

    return clinics;
  } catch (error) {
    console.error("[CK-9902] Error fetching NiV clinics:", {
      errorCode: "CK-9902",
      errorMessage: error.message,
      filters,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// NEW: SOURCE-OF-TRUTH DATA FETCHING
// Reads from clinics collection as primary source
// ============================================

/**
 * Get all clinics for the Niv Dashboard from the actual clinics collection.
 * Primary source: clinics collection
 * Secondary: niv_clinic_management for CRM-only data
 * Enrichment: therapists collection for owner info + patient counts
 */
export const getNivClinicsFromSource = async (filters = {}) => {
  try {
    // 1. Fetch ALL clinics (primary source)
    const clinicsRef = collection(db, "clinics");
    const clinicsSnap = await getDocs(clinicsRef);

    // 2. Fetch ALL management records (CRM data)
    const managementRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const managementSnap = await getDocs(managementRef);
    const managementMap = {};
    managementSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.clinicRef) {
        managementMap[data.clinicRef] = { id: docSnap.id, ...data };
      }
    });

    // 3. Fetch ALL therapists (for owner info)
    const therapistsRef = collection(db, "therapists");
    const therapistsSnap = await getDocs(therapistsRef);
    const therapistsMap = {};
    therapistsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.uid) {
        therapistsMap[data.uid] = {
          fullName: data.fullName || "",
          phone: data.phone || "",
          email: data.email || "",
        };
      }
    });

    // 4. Count patients per clinic (in parallel)
    const patientCountPromises = clinicsSnap.docs.map(async (clinicDoc) => {
      try {
        const patientsRef = collection(db, "clinics", clinicDoc.id, "patients");
        const patientsSnap = await getDocs(patientsRef);
        let activeCount = 0;
        patientsSnap.forEach((pDoc) => {
          // "active" here is the visibility/archive flag — missing field counts as active.
          if (pDoc.data().isActive !== false) activeCount++;
        });
        return { clinicId: clinicDoc.id, total: patientsSnap.size, active: activeCount };
      } catch {
        return { clinicId: clinicDoc.id, total: 0, active: 0 };
      }
    });
    const patientCounts = await Promise.all(patientCountPromises);
    const patientCountMap = {};
    patientCounts.forEach((pc) => {
      patientCountMap[pc.clinicId] = { total: pc.total, active: pc.active };
    });

    // 5. Build unified clinic objects
    const clinics = clinicsSnap.docs.map((clinicDoc) => {
      const clinicData = clinicDoc.data();
      const clinicId = clinicDoc.id;
      const crm = managementMap[clinicId] || null;
      const owner = therapistsMap[clinicData.ownerTherapistId] || {};
      const patients = patientCountMap[clinicId] || { total: 0, active: 0 };
      const planCode = clinicData.plan || "trial";
      const planDetails = SYSTEM_PLANS[planCode] || SYSTEM_PLANS.trial;

      return serializeForRedux({
        // Identity
        clinicId,
        managementId: crm?.id || null,

        // From clinics collection (source of truth)
        name: clinicData.name || "",
        logoUrl: clinicData.logoUrl || null,
        type: clinicData.type || "",
        specializations: clinicData.specializations || [],
        plan: planCode,
        planName: planDetails.nameHebrew,
        isSoloClinic: clinicData.isSoloClinic || false,
        isActive: clinicData.isActive !== false,
        trialEndDate: clinicData.trialEndDate,
        trialStartDate: clinicData.trialStartDate,
        createdAt: clinicData.createdAt,
        updatedAt: clinicData.updatedAt,
        isMorningIntegrated: clinicData.isMorningPaymentSystemIntegrated || false,
        morningBusinessType: clinicData.morningIntegration?.businessType || null,
        morningExempt: clinicData.morningIntegration?.exemption === true || clinicData.morningIntegration?.businessType === "osekPatur",
        morningDefaultDocumentType: clinicData.morningIntegration?.defaultDocumentType ?? null,
        morningSettingsVersion: clinicData.morningIntegration?.settingsVersion ?? 1,
        isGrowIntegrated: clinicData.isGrowIntegrated === true,
        growEnvironment: clinicData.growIntegration?.environment || null,
        // Owner is never in clinic.therapists[] — real count = length + 1
        therapistsCount: (clinicData.therapists?.length || 0) + 1,
        starred: clinicData.starred || false,
        inactiveByAdmin: clinicData.inactiveByAdmin === true,
        inactiveByAdminAt: clinicData.inactiveByAdminAt || null,

        // Owner therapist info
        ownerName: owner.fullName || "",
        ownerPhone: owner.phone || "",
        ownerEmail: owner.email || "",

        // Patient counts
        totalPatients: patients.total,
        activePatients: patients.active,

        // CRM data (from niv_clinic_management, if exists)
        crmStatus: crm?.status || null,
        monthlyPaymentAmount: crm?.monthlyPaymentAmount || null,
        isCustomPayment: crm?.isCustomPayment || false,
        paymentMethod: crm?.paymentMethod || null,
        internalNotes: crm?.internalNotes || null,
        referralSource: crm?.referralSource || null,
        hasCrmRecord: !!crm,
      });
    });

    // 6. Apply filters
    let result = [...clinics];

    if (filters.plan) {
      result = result.filter((c) => c.plan === filters.plan);
    }
    if (filters.crmStatus) {
      result = result.filter((c) => c.crmStatus === filters.crmStatus);
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.ownerName?.toLowerCase().includes(q) ||
          c.ownerPhone?.includes(q)
      );
    }

    // Sort by creation date (newest first)
    result.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });

    return result;
  } catch (error) {
    console.error("[CK-9950] Error fetching clinics from source:", {
      errorCode: "CK-9950",
      errorMessage: error.message,
      filters,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get full detail for a single clinic (for the detail page).
 * Combines clinic doc, patient/appointment stats, AI usage, owner info, and CRM data.
 */
export const getNivClinicFullDetail = async (clinicId) => {
  try {
    // 1. Get clinic document
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (!clinicSnap.exists()) {
      throw new Error("Clinic not found");
    }
    const clinicData = clinicSnap.data();

    // 2. Clinic therapists (single query) — owner info + real counts.
    // clinic.therapists[] never includes the owner, so counts derive from
    // the /therapists docs instead.
    const clinicTherapistsSnap = await getDocs(
      query(collection(db, "therapists"), where("clinicId", "==", clinicId))
    );
    const clinicTherapists = clinicTherapistsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const activeTherapistsCount = clinicTherapists.filter((t) => t.isActive !== false).length;
    let owner = {};
    const ownerDoc = clinicTherapists.find(
      (t) => (t.uid || t.id) === clinicData.ownerTherapistId
    );
    if (ownerDoc) {
      owner = {
        fullName: ownerDoc.fullName || "",
        phone: ownerDoc.phone || "",
        email: ownerDoc.email || "",
      };
    } else if (clinicData.ownerTherapistId) {
      // Fallback: owner doc carries a different clinicId (data drift)
      const tSnap = await getDocs(
        query(collection(db, "therapists"), where("uid", "==", clinicData.ownerTherapistId))
      );
      if (!tSnap.empty) {
        const tData = tSnap.docs[0].data();
        owner = { fullName: tData.fullName || "", phone: tData.phone || "", email: tData.email || "" };
      }
    }

    // 3. Count patients
    const patientsRef = collection(db, "clinics", clinicId, "patients");
    const patientsSnap = await getDocs(patientsRef);
    let activePatients = 0;
    patientsSnap.forEach((d) => {
      // Visibility/archive flag — legacy docs without the field count as active.
      if (d.data().isActive !== false) activePatients++;
    });

    // 4. Count appointments (last 30 days)
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const apptQuery = query(appointmentsRef, where("startTime", ">=", Timestamp.fromDate(thirtyDaysAgo)));
    const apptSnap = await getDocs(apptQuery);

    // 5. Monthly AI/signature usage
    const currentMonthKey = (() => {
      const now = new Date();
      const isr = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" }));
      return `${isr.getFullYear()}-${String(isr.getMonth() + 1).padStart(2, "0")}`;
    })();
    const monthlyUsageRef = doc(db, "clinics", clinicId, "monthlyUsage", currentMonthKey);
    const monthlyUsageSnap = await getDoc(monthlyUsageRef);
    const monthlyUsage = monthlyUsageSnap.exists() ? monthlyUsageSnap.data() : {};

    // All-time usage
    const allUsageRef = collection(db, "clinics", clinicId, "monthlyUsage");
    const allUsageQuery = query(allUsageRef, orderBy("month", "desc"), limit(100));
    const allUsageSnap = await getDocs(allUsageQuery);
    let totalAiRequests = 0, totalAiTokens = 0, totalDigitalSignatures = 0, totalSmsSent = 0;
    let totalVideoMinutes = 0;
    const totalAiByFunction = {};
    const usageHistory = [];
    allUsageSnap.forEach((ds) => {
      const d = ds.data();
      totalAiRequests += d.aiRequestsCount || 0;
      totalAiTokens += d.aiTokensUsed || 0;
      totalDigitalSignatures += d.digitalSignatureCount || 0;
      totalSmsSent += d.smsSentCount || 0;
      totalVideoMinutes += d.videoMeetingMinutes || 0;
      Object.entries(d.aiUsageByFunction || {}).forEach(([fn, v]) => {
        if (!totalAiByFunction[fn]) totalAiByFunction[fn] = { requests: 0, tokens: 0 };
        totalAiByFunction[fn].requests += v?.requests || 0;
        totalAiByFunction[fn].tokens += v?.tokens || 0;
      });
      usageHistory.push({
        month: d.month,
        aiRequestsCount: d.aiRequestsCount || 0,
        aiTokensUsed: d.aiTokensUsed || 0,
        aiUsageByFunction: d.aiUsageByFunction || null,
        digitalSignatureCount: d.digitalSignatureCount || 0,
        smsSentCount: d.smsSentCount || 0,
        smsBySource: d.smsBySource || null,
        videoMeetingMinutes: d.videoMeetingMinutes || 0,
        videoMeetingSessions: d.videoMeetingSessions || 0,
      });
    });

    // 5c. Active rooms count (plan-limit context; non-fatal)
    let roomsCount = null;
    try {
      const roomsSnap = await getDocs(
        query(collection(db, "clinics", clinicId, "rooms"), where("isActive", "!=", false))
      );
      roomsCount = roomsSnap.size;
    } catch (roomsErr) {
      console.warn("[CK-8476] Error counting clinic rooms (non-fatal):", {
        errorCode: "CK-8476",
        errorMessage: roomsErr.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
    }

    // 5d. Multi-clinic v2 — team roles (active therapists only, legacy docs
    // resolved via deriveClinicRole) + pending invites + cross-therapist
    // report views in the last 30 days (count() aggregate; "?" on failure).
    const roleCounts = { owner: 0, manager: 0, therapist: 0, secretary: 0 };
    clinicTherapists
      .filter((t) => t.isActive !== false)
      .forEach((t) => {
        const role = deriveClinicRole(t);
        roleCounts[role] = (roleCounts[role] || 0) + 1;
      });
    const pendingInvitesCount = clinicTherapists.filter((t) => t.inviteStatus === "pending").length;
    let treatmentViews30d = null;
    try {
      const viewsSnap = await getCountFromServer(
        query(
          collection(db, "clinics", clinicId, "treatmentViews"),
          where("viewedAt", ">=", Timestamp.fromDate(thirtyDaysAgo))
        )
      );
      treatmentViews30d = viewsSnap.data().count;
    } catch (viewsErr) {
      console.warn("[CK-8551] Error counting treatment views (non-fatal):", {
        errorCode: "CK-8551",
        errorMessage: viewsErr.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
    }

    // 5b. Per-therapist SMS data — driven by the real /therapists docs
    // (clinic.therapists[] holds partial {id, ...} objects and misses the
    // owner; the old iteration passed objects to doc() and skipped everyone).
    const therapistSmsData = [];
    for (const t of clinicTherapists) {
      try {
        const tUsageRef = doc(db, "therapists", t.id, "monthlyUsage", currentMonthKey);
        const tUsageSnap = await getDoc(tUsageRef);
        const tUsage = tUsageSnap.exists() ? tUsageSnap.data() : {};
        therapistSmsData.push({
          therapistId: t.id,
          fullName: t.fullName || "",
          smsSentCountAllTime: t.smsSentCount || 0,
          smsSentCountThisMonth: tUsage.smsSentCount || 0,
          smsBySourceThisMonth: tUsage.smsBySource || null,
        });
      } catch (tErr) {
        // Skip therapist if error - don't break the whole query
        console.warn("[CK-9951] Error fetching therapist SMS data:", t.id, tErr.message);
      }
    }

    // 6. Get manual CRM layer (niv_clinic_crm, doc id = clinicId)
    const crmSnap = await getDoc(doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId));
    const nivCrm = crmSnap.exists() ? serializeClinicCrm(crmSnap.data()) : null;

    const planCode = clinicData.plan || "trial";
    const planDetails = SYSTEM_PLANS[planCode] || SYSTEM_PLANS.trial;
    // Effective limits = plan defaults ← planOverrides ← legacy per-clinic fields
    const effectiveLimits = getEffectiveLimits(planCode, clinicData);
    const planDefaults = getPlanDefaults(planCode);
    const limitDiffs = diffFromPlan(planCode, clinicData);

    return serializeForRedux({
      // Identity
      clinicId,

      // Clinic data
      name: clinicData.name || "",
      logoUrl: clinicData.logoUrl || null,
      type: clinicData.type || "",
      specializations: clinicData.specializations || [],
      subSpecializations: clinicData.subSpecializations || [],
      ageRange: clinicData.ageRange || [],
      plan: planCode,
      planName: planDetails.nameHebrew,
      planLimits: planDetails.limits,
      planFeatures: planDetails.features,
      // Per-clinic overrides + resolved limits
      planOverrides: clinicData.planOverrides || null,
      effectiveLimits,
      planDefaults,
      limitDiffs,
      videoMeetingMinutesLimit: clinicData.videoMeetingMinutesLimit ?? null,
      aiTreatmentsReportMonthlyLimit: clinicData.aiTreatmentsReportMonthlyLimit ?? null,
      aiTreatmentsReportEnabled: clinicData.aiTreatmentsReportEnabled !== false,
      allowedStorageInMbMaxPerClinic: clinicData.allowedStorageInMbMaxPerClinic ?? null,
      usedStorageInMb: clinicData.usedStorageInMb || 0,
      trialWriteOps: clinicData._trialWriteOps || 0,
      roomsCount,
      isSoloClinic: clinicData.isSoloClinic || false,
      isActive: clinicData.isActive !== false,
      trialEndDate: clinicData.trialEndDate,
      trialStartDate: clinicData.trialStartDate,
      planEndDate: clinicData.planEndDate || null,
      createdAt: clinicData.createdAt,
      updatedAt: clinicData.updatedAt,
      isMorningIntegrated: clinicData.isMorningPaymentSystemIntegrated || false,
        morningBusinessType: clinicData.morningIntegration?.businessType || null,
        morningExempt: clinicData.morningIntegration?.exemption === true || clinicData.morningIntegration?.businessType === "osekPatur",
        morningDefaultDocumentType: clinicData.morningIntegration?.defaultDocumentType ?? null,
        morningSettingsVersion: clinicData.morningIntegration?.settingsVersion ?? 1,
        isGrowIntegrated: clinicData.isGrowIntegrated === true,
        growEnvironment: clinicData.growIntegration?.environment || null,
      therapistsCount:
        clinicTherapists.length || (clinicData.therapists?.length || 0) + 1,
      activeTherapistsCount:
        activeTherapistsCount || (clinicData.therapists?.length || 0) + 1,
      // Churn markers written by the revertExpiredPaidPlans cron
      previousPaidPlan: clinicData.previousPaidPlan || null,
      planRevertedAt: clinicData.planRevertedAt || null,
      defaultHourlyRate: clinicData.defaultHourlyRate || null,
      defaultMeetingTime: clinicData.defaultMeetingTime || 45,
      businessId: clinicData.businessId || null,
      clinicAddress: clinicData.clinicAddress || null,
      vatType: clinicData.vatType || null,
      inactiveByAdmin: clinicData.inactiveByAdmin === true,
      inactiveByAdminAt: clinicData.inactiveByAdminAt || null,

      // Multi-clinic v2 — team & permissions (read-only in NiV)
      multiClinicSettings: clinicData.multiClinicSettings || null,
      permissionsSchemaVersion: clinicData.permissionsSchemaVersion ?? null,
      multiClinicUpgradedAt: clinicData.multiClinicUpgradedAt || null,
      roleCounts,
      pendingInvitesCount,
      treatmentViews30d,

      // Owner
      ownerName: owner.fullName || "",
      ownerPhone: owner.phone || "",
      ownerEmail: owner.email || "",

      // Stats
      totalPatients: patientsSnap.size,
      activePatients,
      appointmentsLast30Days: apptSnap.size,

      // AI Usage
      currentMonthAiRequests: monthlyUsage.aiRequestsCount || 0,
      currentMonthAiTokens: monthlyUsage.aiTokensUsed || 0,
      currentMonthDigitalSignatures: monthlyUsage.digitalSignatureCount || 0,
      totalAiRequests,
      totalAiTokens,
      totalDigitalSignatures,
      totalVideoMinutes,
      usageHistory,
      currentMonthAiByFunction: monthlyUsage.aiUsageByFunction || null,
      totalAiByFunction,

      // AI treatments summary report (per-feature quota; NiV override on clinic doc)
      currentMonthAiTreatmentsReports:
        monthlyUsage.aiUsageByFunction?.generateTreatmentsReportSummary?.requests || 0,

      // Nutrition clinics — client-side AI meal breakdowns
      currentMonthAiFoodEstimates:
        monthlyUsage.aiUsageByFunction?.aiFoodEstimate?.requests || 0,

      // Video Meeting Usage (100ms)
      currentMonthVideoMinutes: monthlyUsage.videoMeetingMinutes || 0,
      currentMonthVideoSessions: monthlyUsage.videoMeetingSessions || 0,

      // SMS Usage
      currentMonthSmsSent: monthlyUsage.smsSentCount || 0,
      currentMonthSmsBySource: monthlyUsage.smsBySource || null,
      totalSmsSent,
      smsPerMonthLimit: effectiveLimits.smsPerMonth,

      // Per-therapist SMS data
      therapistSmsData,

      // Manual CRM layer (niv_clinic_crm)
      nivCrm,
    });
  } catch (error) {
    console.error("[CK-9951] Error fetching clinic full detail:", {
      errorCode: "CK-9951",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/** Israel-timezone "YYYY-MM-DD" for a Firestore Timestamp / Date / ISO string */
const toIsraelDateKey = (value) => {
  if (!value) return null;
  const d =
    typeof value?.toDate === "function" ? value.toDate() : value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

/** ISO string for a Firestore Timestamp / Date / ISO string (null when unparseable) */
const toIsoString = (value) => {
  if (!value) return null;
  const d =
    typeof value?.toDate === "function" ? value.toDate() : value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

// financial_transactions status → money-by-month bucket. `split`/`cancelled`
// are skipped (no cash meaning); `refunded` is counted, not stacked.
const MONEY_BUCKET_BY_STATUS = {
  paid: "paid",
  invoice_manual: "invoiced",
  invoice_auto: "invoiced",
  pending: "pending",
  pending_collection: "pending",
  expected: "expected",
};
const ACTUAL_DATE_STATUSES = new Set(["paid", "invoice_manual", "invoice_auto"]);

/**
 * Per-clinic activity series for the NiV clinic overview:
 *  - daily30: last 30 Israel days of reports generated / signature links /
 *    questionnaire links (zero-filled)
 *  - lastPatient: newest patient (name + createdAt only — no PII beyond name)
 *  - moneyByMonth: last 6 months of financial_transactions grouped by month ×
 *    bucket (paid / invoiced / pending / expected) + refunded count
 * All queries are single-field ranges (no composite index needed).
 * Error Code: CK-8475
 */
export const getNivClinicActivitySeries = async (clinicId, { days = 30, months = 6 } = {}) => {
  try {
    // Ranges are derived from Israel date keys (not the browser's local TZ).
    // Query starts carry a 1-day margin; the per-key bucketing below drops
    // anything outside the exact windows, so the margin never leaks into rows.
    const DAY_MS = 24 * 60 * 60 * 1000;
    const todayKey = toIsraelDateKey(new Date());
    const [ty, tm, td] = todayKey.split("-").map(Number);
    const todayUtc = Date.UTC(ty, tm - 1, td);
    const startDays = new Date(todayUtc - (days - 1) * DAY_MS);
    const startMonths = new Date(Date.UTC(ty, tm - 1 - (months - 1), 1));
    const sub = (name) => collection(db, "clinics", clinicId, name);
    const sinceDays = Timestamp.fromDate(new Date(startDays.getTime() - DAY_MS));
    const sinceMonths = Timestamp.fromDate(new Date(startMonths.getTime() - DAY_MS));

    const [reportsSnap, sigSnap, qSnap, lastPatientSnap, txActualSnap, txExpectedSnap] =
      await Promise.all([
        getDocs(query(sub("generatedReports"), where("createdAt", ">=", sinceDays))),
        getDocs(query(sub("signatureLinks"), where("createdAt", ">=", sinceDays))),
        getDocs(query(sub("questionnaireLinks"), where("createdAt", ">=", sinceDays))),
        getDocs(query(sub("patients"), orderBy("createdAt", "desc"), limit(1))),
        getDocs(query(sub("financial_transactions"), where("actualPaymentDate", ">=", sinceMonths))),
        getDocs(query(sub("financial_transactions"), where("expectedPaymentDate", ">=", sinceMonths))),
      ]);

    // --- daily counts ---
    const dayKeys = [];
    for (let i = 0; i < days; i += 1) {
      // startDays is a UTC midnight; +12h keeps the Israel key stable across DST
      dayKeys.push(toIsraelDateKey(new Date(startDays.getTime() + i * DAY_MS + 12 * 60 * 60 * 1000)));
    }
    const dayIndex = new Map(dayKeys.map((k, i) => [k, i]));
    const daily30 = dayKeys.map((dateKey) => ({
      dateKey,
      reports: 0,
      signatureLinks: 0,
      questionnaireLinks: 0,
    }));
    const bump = (snap, field) => {
      snap.forEach((d) => {
        const key = toIsraelDateKey(d.data().createdAt);
        const idx = key != null ? dayIndex.get(key) : undefined;
        if (idx !== undefined) daily30[idx][field] += 1;
      });
    };
    bump(reportsSnap, "reports");
    bump(sigSnap, "signatureLinks");
    bump(qSnap, "questionnaireLinks");

    // --- last patient ---
    let lastPatient = null;
    if (!lastPatientSnap.empty) {
      const pd = lastPatientSnap.docs[0];
      const p = pd.data();
      lastPatient = {
        id: pd.id,
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        createdAt: toIsoString(p.createdAt),
        isActive: p.isActive !== false,
      };
    }

    // --- money by month ---
    const monthKeys = [];
    for (let i = 0; i < months; i += 1) {
      const d = new Date(Date.UTC(startMonths.getUTCFullYear(), startMonths.getUTCMonth() + i, 1));
      monthKeys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
    }
    const monthRows = monthKeys.map((month) => ({
      month,
      paid: 0,
      invoiced: 0,
      pending: 0,
      expected: 0,
      refundedCount: 0,
      refundedAmount: 0,
      total: 0,
      count: 0,
    }));
    const monthIndex = new Map(monthKeys.map((k, i) => [k, i]));
    const seen = new Set();
    const consume = (d) => {
      if (seen.has(d.id)) return;
      seen.add(d.id);
      const t = d.data();
      const status = t.status || "expected";
      const amount = Number(t.amount) || 0;
      const useActual = ACTUAL_DATE_STATUSES.has(status);
      const dateVal =
        (useActual ? t.actualPaymentDate : t.expectedPaymentDate) ||
        t.actualPaymentDate ||
        t.expectedPaymentDate ||
        t.createdAt;
      const key = toIsraelDateKey(dateVal);
      if (!key) return;
      const idx = monthIndex.get(key.slice(0, 7));
      if (idx === undefined) return;
      const row = monthRows[idx];
      if (status === "refunded") {
        row.refundedCount += 1;
        row.refundedAmount += amount;
        return;
      }
      const bucket = MONEY_BUCKET_BY_STATUS[status];
      if (!bucket) return;
      row[bucket] += amount;
      row.total += amount;
      row.count += 1;
    };
    txActualSnap.forEach(consume);
    txExpectedSnap.forEach(consume);

    return serializeForRedux({
      clinicId,
      daily30,
      lastPatient,
      moneyByMonth: monthRows,
      computedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[CK-8475] Error fetching clinic activity series:", {
      errorCode: "CK-8475",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a field directly on the clinics collection document.
 * Used by Niv admin to edit clinic name, plan, etc.
 */
export const updateClinicFieldDirectly = async (clinicId, fieldUpdates, { skipTimestamp = false } = {}) => {
  try {
    const clinicRef = doc(db, "clinics", clinicId);
    // planOverrides: null → remove the map entirely (rules accept an absent key)
    const normalized =
      fieldUpdates && "planOverrides" in fieldUpdates && fieldUpdates.planOverrides == null
        ? { ...fieldUpdates, planOverrides: deleteField() }
        : fieldUpdates;
    await updateDoc(clinicRef, {
      ...normalized,
      // skipTimestamp: don't bump updatedAt for non-activity edits (e.g. starring),
      // since updatedAt is the activity proxy used by churn alerts and sorting.
      ...(skipTimestamp ? {} : { updatedAt: serverTimestamp() }),
    });
    return { clinicId, ...fieldUpdates };
  } catch (error) {
    console.error("[CK-9952] Error updating clinic field:", {
      errorCode: "CK-9952",
      errorMessage: error.message,
      clinicId,
      fieldUpdates,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Ensure a CRM management record exists for a clinic.
 * Creates one if it doesn't exist. Returns the management ID.
 */
export const ensureCrmRecord = async (clinicId) => {
  try {
    const mgmtRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const mgmtQuery = query(mgmtRef, where("clinicRef", "==", clinicId));
    const mgmtSnap = await getDocs(mgmtQuery);

    if (!mgmtSnap.empty) {
      return mgmtSnap.docs[0].id;
    }

    // Create lean CRM record
    const newDocRef = doc(mgmtRef);
    const now = serverTimestamp();
    await setDoc(newDocRef, {
      id: newDocRef.id,
      clinicRef: clinicId,
      status: CLINIC_STATUSES.TRIAL,
      statusHistory: [{
        status: CLINIC_STATUSES.TRIAL,
        changedAt: Timestamp.now(),
        note: "נוצר אוטומטית",
      }],
      monthlyPaymentAmount: null,
      isCustomPayment: false,
      paymentMethod: null,
      paymentHistory: [],
      internalNotes: null,
      callLogs: [],
      referralSource: null,
      contractRenewalDate: null,
      lastContactDate: null,
      createdAt: now,
      updatedAt: now,
    });

    return newDocRef.id;
  } catch (error) {
    console.error("[CK-9953] Error ensuring CRM record:", {
      errorCode: "CK-9953",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a NiV clinic management record
 */
export const updateNivClinicManagement = async (managementId, data) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT, managementId);

    const updateData = {
      ...data,
      updatedAt: serverTimestamp(),
    };

    // Remove undefined values
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(docRef, updateData);

    return { id: managementId, ...updateData };
  } catch (error) {
    console.error("[CK-9905] Error updating NiV clinic management:", {
      errorCode: "CK-9905",
      errorMessage: error.message,
      managementId,
      data,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update clinic status with history tracking
 */
export const updateNivClinicStatus = async (
  managementId,
  newStatus,
  note = null
) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT, managementId);

    const statusEntry = {
      status: newStatus,
      changedAt: Timestamp.now(),
      note: note,
    };

    await updateDoc(docRef, {
      status: newStatus,
      statusHistory: arrayUnion(statusEntry),
      updatedAt: serverTimestamp(),
    });

    return { id: managementId, status: newStatus };
  } catch (error) {
    console.error("[CK-9905] Error updating NiV clinic status:", {
      errorCode: "CK-9905",
      errorMessage: error.message,
      managementId,
      newStatus,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Add a call log to a clinic management record
 */
export const addNivCallLog = async (managementId, logData) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT, managementId);

    const callLog = {
      id: generateId(),
      date: logData.date
        ? Timestamp.fromDate(new Date(logData.date))
        : Timestamp.now(),
      summary: logData.summary,
      createdAt: Timestamp.now(),
    };

    await updateDoc(docRef, {
      callLogs: arrayUnion(callLog),
      lastContactDate: Timestamp.now(),
      updatedAt: serverTimestamp(),
    });

    return serializeForRedux(callLog);
  } catch (error) {
    console.error("[CK-9906] Error adding NiV call log:", {
      errorCode: "CK-9906",
      errorMessage: error.message,
      managementId,
      logData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Add a payment record to a clinic management record
 */
export const addNivPaymentRecord = async (managementId, paymentData) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT, managementId);

    const payment = {
      month: paymentData.month, // "2026-01"
      status: paymentData.status || PAYMENT_STATUSES.PAID,
      paidAt: paymentData.paidAt
        ? Timestamp.fromDate(new Date(paymentData.paidAt))
        : Timestamp.now(),
      amount: paymentData.amount || 0,
      note: paymentData.note || null,
    };

    await updateDoc(docRef, {
      paymentHistory: arrayUnion(payment),
      updatedAt: serverTimestamp(),
    });

    return serializeForRedux(payment);
  } catch (error) {
    console.error("[CK-9907] Error adding NiV payment record:", {
      errorCode: "CK-9907",
      errorMessage: error.message,
      managementId,
      paymentData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * @deprecated SMS quota is now determined by the plan's smsPerMonth limit.
 * No longer uses smsTokens. Kept for backward compatibility.
 */
export const updateClinicSmsLimit = async (
  managementId,
  clinicId,
  newLimit
) => {
  console.warn("[CK-9908] updateClinicSmsLimit is deprecated. SMS quota is determined by plan.");
  return { managementId, clinicId, newLimit };
};

// ============================================
// NIV TASKS - CRUD
// ============================================

/**
 * Create a new NiV task
 */
export const createNivTask = async (taskData) => {
  try {
    const collectionRef = collection(db, NIV_COLLECTIONS.TASKS);
    const newDocRef = doc(collectionRef);

    const now = serverTimestamp();

    const docData = {
      id: newDocRef.id,
      title: taskData.title,
      description: taskData.description || null,
      status: taskData.status || TASK_STATUSES.TODO,
      priority: taskData.priority || TASK_PRIORITIES.MEDIUM,
      dueDate: taskData.dueDate
        ? Timestamp.fromDate(new Date(taskData.dueDate))
        : null,
      clinicRef: taskData.clinicRef || null,
      clinicName: taskData.clinicName || null,
      listId: taskData.listId || null,
      type: taskData.type || null,
      order: taskData.order ?? Date.now(),
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(newDocRef, docData);

    return serializeForRedux({ id: newDocRef.id, ...docData });
  } catch (error) {
    console.error("[CK-9910] Error creating NiV task:", {
      errorCode: "CK-9910",
      errorMessage: error.message,
      taskData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all NiV tasks with optional filters
 */
export const getNivTasks = async (filters = {}) => {
  try {
    const collectionRef = collection(db, NIV_COLLECTIONS.TASKS);
    let q = query(collectionRef, orderBy("createdAt", "desc"));

    // Apply status filter
    if (filters.status && filters.status !== "all") {
      q = query(
        collectionRef,
        where("status", "==", filters.status),
        orderBy("createdAt", "desc")
      );
    }

    // Apply limit
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const querySnapshot = await getDocs(q);
    const tasks = [];

    querySnapshot.forEach((doc) => {
      // Skip the tasks config doc (stored in the same collection)
      if (doc.id === TASKS_CONFIG_DOC_ID || doc.data().isConfig) return;
      tasks.push(serializeForRedux({ id: doc.id, ...doc.data() }));
    });

    return tasks;
  } catch (error) {
    console.error("[CK-9913] Error fetching NiV tasks:", {
      errorCode: "CK-9913",
      errorMessage: error.message,
      filters,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get a single NiV task
 */
export const getNivTask = async (taskId) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.TASKS, taskId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: docSnap.id, ...docSnap.data() });
  } catch (error) {
    console.error("[CK-9914] Error fetching NiV task:", {
      errorCode: "CK-9914",
      errorMessage: error.message,
      taskId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a NiV task
 */
export const updateNivTask = async (taskId, data) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.TASKS, taskId);

    const updateData = {
      ...data,
      updatedAt: serverTimestamp(),
    };

    // Convert dueDate if provided
    if (data.dueDate) {
      updateData.dueDate = Timestamp.fromDate(new Date(data.dueDate));
    }

    // Remove undefined values
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(docRef, updateData);

    return { id: taskId, ...updateData };
  } catch (error) {
    console.error("[CK-9911] Error updating NiV task:", {
      errorCode: "CK-9911",
      errorMessage: error.message,
      taskId,
      data,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a NiV task
 */
export const deleteNivTask = async (taskId) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.TASKS, taskId);
    await deleteDoc(docRef);

    return { id: taskId };
  } catch (error) {
    console.error("[CK-9912] Error deleting NiV task:", {
      errorCode: "CK-9912",
      errorMessage: error.message,
      taskId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// NIV TASKS - BOARD CONFIG (lists + option sets)
// ============================================

export const TASKS_CONFIG_DOC_ID = "_config";

export const DEFAULT_TASKS_CONFIG = {
  lists: [
    { id: "l_todo", name: "To-Do", color: "#579bfc", collapsed: false, order: 1000 },
    { id: "l_completed", name: "Completed", color: "#00c875", collapsed: false, order: 2000 },
  ],
  options: {
    status: [
      { id: "todo", label: "To Do", color: "#c4c4c4", isDone: false },
      { id: "in_progress", label: "In Progress", color: "#fdab3d", isDone: false },
      { id: "done", label: "Done", color: "#00c875", isDone: true },
    ],
    priority: [
      { id: "low", label: "Low", color: "#579bfc" },
      { id: "medium", label: "Medium", color: "#fdab3d" },
      { id: "high", label: "High", color: "#e2445c" },
    ],
    type: [
      { id: "bug", label: "Bug", color: "#e2445c" },
      { id: "feature", label: "Feature", color: "#00c875" },
      { id: "quality", label: "Quality", color: "#ff5ac4" },
      { id: "infra", label: "Infrastructure", color: "#784bd1" },
    ],
  },
};

/**
 * Get the NiV tasks board config (lists + status/priority/type option sets).
 * Returns null if the config doc doesn't exist yet (caller seeds it).
 */
export const getNivTasksConfig = async () => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.TASKS, TASKS_CONFIG_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return serializeForRedux({ id: docSnap.id, ...docSnap.data() });
  } catch (error) {
    console.error("[CK-9915] Error fetching NiV tasks config:", {
      errorCode: "CK-9915",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create/update the NiV tasks board config (merge write)
 */
export const updateNivTasksConfig = async (data) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.TASKS, TASKS_CONFIG_DOC_ID);

    await setDoc(
      docRef,
      {
        ...data,
        isConfig: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return { id: TASKS_CONFIG_DOC_ID, ...data };
  } catch (error) {
    console.error("[CK-9916] Error updating NiV tasks config:", {
      errorCode: "CK-9916",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Bulk-update NiV tasks (e.g. reassign option/list before deleting it)
 */
export const bulkUpdateNivTasks = async (taskIds, data) => {
  try {
    const batch = writeBatch(db);

    taskIds.forEach((taskId) => {
      const docRef = doc(db, NIV_COLLECTIONS.TASKS, taskId);
      batch.update(docRef, { ...data, updatedAt: serverTimestamp() });
    });

    await batch.commit();

    return { taskIds, ...data };
  } catch (error) {
    console.error("[CK-9917] Error bulk updating NiV tasks:", {
      errorCode: "CK-9917",
      errorMessage: error.message,
      taskIds,
      data,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// NIV STATS & ALERTS
// ============================================

/**
 * Get dashboard statistics
 */
export const getNivDashboardStats = async () => {
  try {
    // Get all management records
    const managementRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const managementSnap = await getDocs(managementRef);

    let totalClinics = 0;
    let activeClinics = 0;
    let trialClinics = 0;
    let suspendedClinics = 0;
    let churnedClinics = 0;
    let totalMonthlyRevenue = 0;
    let latePayments = 0;

    const currentMonth = getCurrentMonthString();

    managementSnap.forEach((doc) => {
      const data = doc.data();
      totalClinics++;

      switch (data.status) {
        case CLINIC_STATUSES.ACTIVE:
          activeClinics++;
          if (data.monthlyPaymentAmount) {
            totalMonthlyRevenue += data.monthlyPaymentAmount;
          }
          break;
        case CLINIC_STATUSES.TRIAL:
          trialClinics++;
          break;
        case CLINIC_STATUSES.SUSPENDED:
          suspendedClinics++;
          break;
        case CLINIC_STATUSES.CHURNED:
          churnedClinics++;
          break;
      }

      // Check for late payments (active clinics without payment for current month)
      if (data.status === CLINIC_STATUSES.ACTIVE && data.paymentHistory) {
        const currentMonthPayment = data.paymentHistory.find(
          (p) => p.month === currentMonth && p.status === PAYMENT_STATUSES.PAID
        );
        if (!currentMonthPayment) {
          latePayments++;
        }
      }
    });

    // Get open tasks count
    const tasksRef = collection(db, NIV_COLLECTIONS.TASKS);
    const openTasksQuery = query(
      tasksRef,
      where("status", "!=", TASK_STATUSES.DONE)
    );
    const openTasksSnap = await getDocs(openTasksQuery);
    const openTasks = openTasksSnap.size;

    return {
      totalClinics,
      activeClinics,
      trialClinics,
      suspendedClinics,
      churnedClinics,
      totalMonthlyRevenue,
      latePayments,
      openTasks,
    };
  } catch (error) {
    console.error("[CK-9900] Error fetching NiV dashboard stats:", {
      errorCode: "CK-9900",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get urgent alerts for the dashboard
 */
export const getNivAlerts = async () => {
  try {
    const alerts = [];
    const now = new Date();
    const currentMonth = getCurrentMonthString();

    // Get all management records
    const managementRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const managementSnap = await getDocs(managementRef);

    const latePaymentClinics = [];
    const trialsEndingSoon = [];
    const lowSmsClinics = [];

    managementSnap.forEach((doc) => {
      const data = doc.data();

      // Check late payments
      if (data.status === CLINIC_STATUSES.ACTIVE && data.paymentHistory) {
        const currentMonthPayment = data.paymentHistory.find(
          (p) => p.month === currentMonth && p.status === PAYMENT_STATUSES.PAID
        );
        if (!currentMonthPayment) {
          latePaymentClinics.push({
            id: doc.id,
            clinicName: data.clinicName,
            clinicRef: data.clinicRef,
          });
        }
      }

      // Check trials ending soon (within 7 days)
      // We need to fetch this from the actual clinic document
      if (data.status === CLINIC_STATUSES.TRIAL) {
        trialsEndingSoon.push({
          id: doc.id,
          clinicName: data.clinicName,
          clinicRef: data.clinicRef,
        });
      }
    });

    // Fetch actual clinic data for SMS and trial end dates
    const clinicsRef = collection(db, "clinics");
    const clinicsSnap = await getDocs(clinicsRef);

    const currentMonthKey = getCurrentMonthKey();

    // Fetch monthly usage for all clinics to check SMS quotas
    for (const clinicDoc of clinicsSnap.docs) {
      const data = clinicDoc.data();
      const planCode = (data.plan || "trial").toLowerCase();
      const planDetails = SYSTEM_PLANS[planCode] || SYSTEM_PLANS.trial;
      const smsLimit = planDetails.limits?.smsPerMonth || 50;

      // Get current month SMS usage
      try {
        const usageRef = doc(db, "clinics", clinicDoc.id, "monthlyUsage", currentMonthKey);
        const usageSnap = await getDoc(usageRef);
        const smsSent = usageSnap.exists() ? usageSnap.data().smsSentCount || 0 : 0;
        const smsRemaining = Math.max(0, smsLimit - smsSent);
        const usagePercent = smsLimit > 0 ? Math.round((smsSent / smsLimit) * 100) : 0;

        // Alert if usage >= 80% of quota
        if (usagePercent >= 80) {
          lowSmsClinics.push({
            clinicId: clinicDoc.id,
            clinicName: data.name,
            smsSent,
            smsLimit,
            smsRemaining,
            usagePercent,
          });
        }
      } catch (usageErr) {
        // Skip on error
      }
    }

    clinicsSnap.forEach((doc) => {
      const data = doc.data();

      // Check trial ending soon
      if (data.plan === "trial" && data.trialEndDate) {
        const trialEnd = data.trialEndDate.toDate
          ? data.trialEndDate.toDate()
          : new Date(data.trialEndDate);
        const daysUntilEnd = Math.ceil(
          (trialEnd - now) / (1000 * 60 * 60 * 24)
        );
        if (daysUntilEnd <= 7 && daysUntilEnd > 0) {
          const existing = trialsEndingSoon.find((t) => t.clinicRef === doc.id);
          if (existing) {
            existing.daysLeft = daysUntilEnd;
            existing.trialEndDate = trialEnd.toISOString();
          }
        }
      }
    });

    // Build alerts array
    if (latePaymentClinics.length > 0) {
      alerts.push({
        id: "late_payments",
        type: "error",
        title: `${latePaymentClinics.length} קליניקות עם תשלום באיחור`,
        clinics: latePaymentClinics,
        priority: 1,
      });
    }

    const validTrials = trialsEndingSoon.filter((t) => t.daysLeft);
    if (validTrials.length > 0) {
      alerts.push({
        id: "trials_ending",
        type: "warning",
        title: `${validTrials.length} טריילים שנגמרים השבוע`,
        clinics: validTrials,
        priority: 2,
      });
    }

    if (lowSmsClinics.length > 0) {
      alerts.push({
        id: "low_sms",
        type: "warning",
        title: `${lowSmsClinics.length} קליניקות עם שימוש גבוה ב-SMS (≥80% מהמכסה)`,
        clinics: lowSmsClinics,
        priority: 3,
      });
    }

    // Sort by priority
    alerts.sort((a, b) => a.priority - b.priority);

    return alerts;
  } catch (error) {
    console.error("[CK-9901] Error fetching NiV alerts:", {
      errorCode: "CK-9901",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Gets the current month key in YYYY-MM format (Israel timezone)
 * @returns {string} - Month key (e.g., "2026-01")
 */
const getCurrentMonthKey = () => {
  const now = new Date();
  const israelDate = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Jerusalem" })
  );
  const year = israelDate.getFullYear();
  const month = String(israelDate.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

/**
 * Get usage statistics for a specific clinic
 * Includes AI usage and digital signature tracking (monthly)
 */
export const getClinicUsageStats = async (clinicId) => {
  try {
    // Get clinic document
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicSnap = await getDoc(clinicRef);

    if (!clinicSnap.exists()) {
      throw new Error("Clinic not found");
    }

    const clinicData = clinicSnap.data();

    // Count patients
    const patientsRef = collection(db, "clinics", clinicId, "patients");
    const patientsSnap = await getDocs(patientsRef);
    const totalPatients = patientsSnap.size;
    let activePatients = 0;
    patientsSnap.forEach((doc) => {
      if (doc.data().status === "active") {
        activePatients++;
      }
    });

    // Count appointments (last 30 days)
    const appointmentsRef = collection(db, "clinics", clinicId, "appointments");
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const appointmentsQuery = query(
      appointmentsRef,
      where("startTime", ">=", Timestamp.fromDate(thirtyDaysAgo))
    );
    const appointmentsSnap = await getDocs(appointmentsQuery);
    const appointmentsLast30Days = appointmentsSnap.size;

    // Count therapists
    const therapistsCount = clinicData.therapists?.length || 1;

    // Get last login from therapists
    let lastLogin = null;
    if (clinicData.therapists && clinicData.therapists.length > 0) {
      // We would need to query therapists collection for this
      // For now, use clinic's updatedAt as proxy
      lastLogin = clinicData.updatedAt;
    }

    // Get current month usage (AI + Digital Signatures)
    const currentMonthKey = getCurrentMonthKey();
    const monthlyUsageRef = doc(db, "clinics", clinicId, "monthlyUsage", currentMonthKey);
    const monthlyUsageSnap = await getDoc(monthlyUsageRef);
    const currentMonthUsage = monthlyUsageSnap.exists() ? monthlyUsageSnap.data() : null;

    // Get all-time usage totals (sum all monthly docs)
    const allUsageRef = collection(db, "clinics", clinicId, "monthlyUsage");
    const allUsageQuery = query(allUsageRef, orderBy("month", "desc"), limit(100));
    const allUsageSnap = await getDocs(allUsageQuery);
    
    let totalAiRequests = 0;
    let totalAiTokens = 0;
    let totalDigitalSignatures = 0;
    let totalSmsSent = 0;
    const totalAiByFunction = {};
    const usageHistory = [];

    allUsageSnap.forEach((docSnap) => {
      const data = docSnap.data();
      totalAiRequests += data.aiRequestsCount || 0;
      totalAiTokens += data.aiTokensUsed || 0;
      totalDigitalSignatures += data.digitalSignatureCount || 0;
      totalSmsSent += data.smsSentCount || 0;
      // Aggregate AI usage per function (all-time)
      if (data.aiUsageByFunction && typeof data.aiUsageByFunction === "object") {
        Object.entries(data.aiUsageByFunction).forEach(([fn, v]) => {
          if (!totalAiByFunction[fn]) totalAiByFunction[fn] = { requests: 0, tokens: 0 };
          totalAiByFunction[fn].requests += v?.requests || 0;
          totalAiByFunction[fn].tokens += v?.tokens || 0;
        });
      }
      usageHistory.push({
        month: data.month,
        aiRequestsCount: data.aiRequestsCount || 0,
        aiTokensUsed: data.aiTokensUsed || 0,
        digitalSignatureCount: data.digitalSignatureCount || 0,
        smsSentCount: data.smsSentCount || 0,
        smsBySource: data.smsBySource || null,
        aiUsageByFunction: data.aiUsageByFunction || null,
      });
    });

    // Calculate SMS quota
    const planCode = (clinicData.plan || "trial").toLowerCase();
    const planInfo = SYSTEM_PLANS[planCode] || SYSTEM_PLANS.trial;
    const smsPerMonthLimit = planInfo.limits?.smsPerMonth || 50;
    const smsUsedThisMonth = currentMonthUsage?.smsSentCount || 0;
    const smsRemaining = Math.max(0, smsPerMonthLimit - smsUsedThisMonth);

    return {
      clinicId,
      clinicName: clinicData.name,
      plan: clinicData.plan,
      smsPerMonthLimit,
      smsUsedThisMonth,
      smsRemaining,
      totalPatients,
      activePatients,
      appointmentsLast30Days,
      therapistsCount,
      isSoloClinic: clinicData.isSoloClinic,
      createdAt: clinicData.createdAt,
      trialEndDate: clinicData.trialEndDate,
      isMorningIntegrated: clinicData.isMorningPaymentSystemIntegrated || false,
        morningBusinessType: clinicData.morningIntegration?.businessType || null,
        morningExempt: clinicData.morningIntegration?.exemption === true || clinicData.morningIntegration?.businessType === "osekPatur",
        morningDefaultDocumentType: clinicData.morningIntegration?.defaultDocumentType ?? null,
        morningSettingsVersion: clinicData.morningIntegration?.settingsVersion ?? 1,
        isGrowIntegrated: clinicData.isGrowIntegrated === true,
        growEnvironment: clinicData.growIntegration?.environment || null,
      lastLogin: lastLogin ? serializeForRedux({ date: lastLogin }).date : null,
      // AI Usage - Current Month
      currentMonthAiRequests: currentMonthUsage?.aiRequestsCount || 0,
      currentMonthAiTokens: currentMonthUsage?.aiTokensUsed || 0,
      currentMonthDigitalSignatures: currentMonthUsage?.digitalSignatureCount || 0,
      currentMonthAiByFunction: currentMonthUsage?.aiUsageByFunction || null,
      // SMS Usage - Current Month
      currentMonthSmsSent: currentMonthUsage?.smsSentCount || 0,
      currentMonthSmsBySource: currentMonthUsage?.smsBySource || null,
      // AI Usage - All Time Totals
      totalAiRequests,
      totalAiTokens,
      totalAiByFunction,
      totalDigitalSignatures,
      // SMS Usage - All Time
      totalSmsSent,
      // Usage History (for charts/reports)
      usageHistory,
    };
  } catch (error) {
    console.error("[CK-9920] Error fetching clinic usage stats:", {
      errorCode: "CK-9920",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all clinics from the main clinics collection (for creating management records)
 */
export const getAllClinicsBasic = async () => {
  try {
    const clinicsRef = collection(db, "clinics");
    const clinicsSnap = await getDocs(clinicsRef);

    const clinics = [];
    clinicsSnap.forEach((doc) => {
      const data = doc.data();
      clinics.push({
        id: doc.id,
        name: data.name,
        plan: data.plan,
        ownerTherapistId: data.ownerTherapistId,
        createdAt: serializeForRedux({ date: data.createdAt }).date,
        trialEndDate: data.trialEndDate
          ? serializeForRedux({ date: data.trialEndDate }).date
          : null,
      });
    });

    return clinics;
  } catch (error) {
    console.error("[CK-9921] Error fetching all clinics:", {
      errorCode: "CK-9921",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all onboarding call requests (clinics with onboardingCallDate set)
 */
export const getOnboardingRequests = async () => {
  try {
    const clinicsRef = collection(db, "clinics");
    const clinicsSnap = await getDocs(clinicsRef);

    const requests = [];
    clinicsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      // Only include clinics that have requested onboarding call
      if (data.onboardingCallDate) {
        requests.push({
          clinicId: docSnap.id,
          clinicName: data.name,
          onboardingCallDate: data.onboardingCallDate,
          ownerTherapistId: data.ownerTherapistId,
          createdAt: serializeForRedux({ date: data.createdAt }).date,
          // Status: pending if date is in the future, done if in the past
          status:
            new Date(data.onboardingCallDate) > new Date() ? "pending" : "past",
        });
      }
    });

    // Also fetch therapist info for contact details
    const therapistsRef = collection(db, "therapists");
    const therapistsSnap = await getDocs(therapistsRef);
    const therapistsMap = {};
    therapistsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      therapistsMap[data.uid] = {
        fullName: data.fullName,
        phone: data.phone,
        email: data.email,
      };
    });

    // Enrich requests with therapist info
    const enrichedRequests = requests.map((req) => ({
      ...req,
      therapist: therapistsMap[req.ownerTherapistId] || null,
    }));

    // Sort by date (upcoming first)
    enrichedRequests.sort((a, b) => {
      const dateA = new Date(a.onboardingCallDate);
      const dateB = new Date(b.onboardingCallDate);
      return dateA - dateB;
    });

    return enrichedRequests;
  } catch (error) {
    console.error("[CK-9922] Error fetching onboarding requests:", {
      errorCode: "CK-9922",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mark onboarding call as completed (removes the date from clinic)
 */
export const markOnboardingCompleted = async (clinicId) => {
  try {
    const clinicRef = doc(db, "clinics", clinicId);
    await updateDoc(clinicRef, {
      onboardingCallDate: null,
      onboardingCallCompleted: true,
      onboardingCallCompletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { clinicId, completed: true };
  } catch (error) {
    console.error("[CK-9923] Error marking onboarding completed:", {
      errorCode: "CK-9923",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// CHURN RISK RESOLUTIONS
// ============================================
// Retention-risk alerts are derived (computed from clinic activity), not stored
// docs. To let an admin mark one "handled" (טופל) and hide it, we persist a
// dismissal keyed by `${clinicId}_${level}` (level = trial|inactive|gone).

/**
 * Mark a churn-risk alert as resolved/handled. Permanent dismiss for that
 * clinic + level until the clinic re-enters a different churn bucket.
 */
export const resolveChurnAlert = async (clinicId, level) => {
  try {
    const docId = `${clinicId}_${level}`;
    const docRef = doc(db, NIV_COLLECTIONS.CHURN_RESOLUTIONS, docId);
    await setDoc(docRef, {
      clinicId,
      level,
      resolvedAt: serverTimestamp(),
    });
    return { clinicId, level, resolved: true };
  } catch (error) {
    console.error("[CK-9928] Error resolving churn alert:", {
      errorCode: "CK-9928",
      errorMessage: error.message,
      clinicId,
      level,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all resolved churn alerts. Returns array of { clinicId, level }.
 */
export const getChurnResolutions = async () => {
  try {
    const collectionRef = collection(db, NIV_COLLECTIONS.CHURN_RESOLUTIONS);
    const snap = await getDocs(collectionRef);
    const resolutions = [];
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      resolutions.push({ clinicId: data.clinicId, level: data.level });
    });
    return resolutions;
  } catch (error) {
    console.error("[CK-9929] Error fetching churn resolutions:", {
      errorCode: "CK-9929",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// STAFF THERAPIST ADDITIONS
// ============================================
// Docs are created server-side by the onTherapistCreatedNotifyAdmin trigger
// whenever an existing clinic adds a staff therapist. They stay "pending"
// (shown on the morning briefing) until the admin marks them resolved.

/**
 * Get all staff-therapist addition alerts, newest first.
 */
export const getTherapistAdditions = async () => {
  try {
    const additionsRef = collection(db, NIV_COLLECTIONS.THERAPIST_ADDITIONS);
    const q = query(additionsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    const additions = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      additions.push({
        id: docSnap.id,
        ...data,
        createdAt: serializeForRedux({ date: data.createdAt }).date,
        resolvedAt: serializeForRedux({ date: data.resolvedAt }).date,
      });
    });

    return additions;
  } catch (error) {
    console.error("[CK-9881] Error getting therapist additions:", {
      errorCode: "CK-9881",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mark a staff-therapist addition alert as resolved (טופל).
 */
export const resolveTherapistAddition = async (additionId) => {
  try {
    const additionRef = doc(db, NIV_COLLECTIONS.THERAPIST_ADDITIONS, additionId);
    await updateDoc(additionRef, {
      status: THERAPIST_ADDITION_STATUSES.RESOLVED,
      resolvedAt: serverTimestamp(),
    });
    return { id: additionId, status: THERAPIST_ADDITION_STATUSES.RESOLVED };
  } catch (error) {
    console.error("[CK-9882] Error resolving therapist addition:", {
      errorCode: "CK-9882",
      errorMessage: error.message,
      additionId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// CALL REQUESTS MANAGEMENT
// ============================================

/**
 * Create a new call request
 */
export const createCallRequest = async (requestData) => {
  try {
    const callRequestsRef = collection(db, NIV_COLLECTIONS.CALL_REQUESTS);
    const newRequestRef = doc(callRequestsRef);
    
    const callRequest = {
      id: newRequestRef.id,
      phone: requestData.phone,
      fullName: requestData.fullName || null,
      clinicName: requestData.clinicName || null,
      email: requestData.email || null,
      status: CALL_REQUEST_STATUSES.PENDING,
      source: requestData.source || "signup", // signup, dashboard, etc.
      notes: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newRequestRef, callRequest);

    return {
      ...callRequest,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error("[CK-9924] Error creating call request:", {
      errorCode: "CK-9924",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a lead from a public page (homepage lead-form section).
 * Unauthenticated callers — Firestore rule on `niv_call_requests` permits this
 * for `source` in {'homepage_lead','homepage_contact','homepage_solutions','get_started_page'} with strict schema.
 * `profession` (≤60 chars) and `utm` (map, ≤8 keys) are optional extras from `/start`.
 */
export const createPublicLead = async ({ fullName, phone, email, message, source, clinicName, profession, utm }) => {
  try {
    const cleaned = (phone || "").replace(/\D/g, "");
    let normalizedPhone = phone || "";
    if (cleaned.length === 10 && cleaned.startsWith("0")) {
      normalizedPhone = `+972${cleaned.slice(1)}`;
    } else if (cleaned.startsWith("972")) {
      normalizedPhone = `+${cleaned}`;
    }

    const callRequestsRef = collection(db, NIV_COLLECTIONS.CALL_REQUESTS);
    const newRequestRef = doc(callRequestsRef);

    const lead = {
      id: newRequestRef.id,
      phone: normalizedPhone,
      fullName: (fullName || "").trim() || null,
      clinicName: (clinicName || "").trim() || null,
      email: (email || "").trim().toLowerCase() || null,
      message: (message || "").trim() || null,
      status: CALL_REQUEST_STATUSES.PENDING,
      source: source || "homepage_lead",
      notes: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // /start page extras — written only when provided (rules allow-list them;
    // never write `undefined`). Existing callers are unaffected.
    if (typeof profession === "string" && profession.trim()) {
      lead.profession = profession.trim().slice(0, 60);
    }
    if (utm && typeof utm === "object" && !Array.isArray(utm)) {
      const cleanUtm = {};
      Object.entries(utm).forEach(([k, v]) => {
        if (typeof v === "string" && v.trim()) cleanUtm[k.slice(0, 32)] = v.trim().slice(0, 200);
      });
      if (Object.keys(cleanUtm).length > 0) lead.utm = cleanUtm;
    }

    // Public-page traffic snapshot (referrer / device / time-to-lead, no PII) —
    // null outside tracked marketing pages (e.g. the dashboard trial popup).
    const analyticsSnapshot = getLeadAnalyticsSnapshot();
    if (analyticsSnapshot) lead.analytics = analyticsSnapshot;

    await setDoc(newRequestRef, lead);

    trackPublicLead(lead.source);

    return { ...lead, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  } catch (error) {
    console.error("[CK-1050] Error creating public lead:", {
      errorCode: "CK-1050",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all call requests
 */
export const getCallRequests = async () => {
  try {
    const callRequestsRef = collection(db, NIV_COLLECTIONS.CALL_REQUESTS);
    const q = query(callRequestsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    const requests = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      requests.push({
        id: docSnap.id,
        ...data,
        createdAt: serializeForRedux({ date: data.createdAt }).date,
        updatedAt: serializeForRedux({ date: data.updatedAt }).date,
        conversationLogs: (data.conversationLogs || []).map((log) => ({
          ...log,
          createdAt: serializeForRedux({ date: log.createdAt }).date,
        })),
      });
    });

    return requests;
  } catch (error) {
    console.error("[CK-9925] Error getting call requests:", {
      errorCode: "CK-9925",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update call request status
 */
export const updateCallRequestStatus = async (requestId, status, notes = null) => {
  try {
    const requestRef = doc(db, NIV_COLLECTIONS.CALL_REQUESTS, requestId);
    
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
    };
    
    if (notes !== null) {
      updateData.notes = notes;
    }

    await updateDoc(requestRef, updateData);

    return { id: requestId, status, notes };
  } catch (error) {
    console.error("[CK-9926] Error updating call request:", {
      errorCode: "CK-9926",
      errorMessage: error.message,
      requestId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete call request
 */
export const deleteCallRequest = async (requestId) => {
  try {
    const requestRef = doc(db, NIV_COLLECTIONS.CALL_REQUESTS, requestId);
    await deleteDoc(requestRef);
    return { id: requestId, deleted: true };
  } catch (error) {
    console.error("[CK-9927] Error deleting call request:", {
      errorCode: "CK-9927",
      errorMessage: error.message,
      requestId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Append a conversation-update note to a call request.
 * Notes are stored newest-anywhere in a conversationLogs array on the doc.
 */
export const addCallRequestNote = async (requestId, text) => {
  try {
    const requestRef = doc(db, NIV_COLLECTIONS.CALL_REQUESTS, requestId);
    const note = {
      id: generateId(),
      text,
      createdAt: Timestamp.now(),
    };
    await updateDoc(requestRef, {
      conversationLogs: arrayUnion(note),
      updatedAt: serverTimestamp(),
    });
    return { id: requestId, note };
  } catch (error) {
    console.error("[CK-9928] Error adding call request note:", {
      errorCode: "CK-9928",
      errorMessage: error.message,
      requestId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// PLANS MANAGEMENT
// ============================================

/**
 * Get all plans
 */
export const getNivPlans = async () => {
  try {
    const plansRef = collection(db, NIV_COLLECTIONS.PLANS);
    const plansQuery = query(plansRef, orderBy("sortOrder", "asc"));
    const plansSnap = await getDocs(plansQuery);

    const plans = [];
    plansSnap.forEach((docSnap) => {
      plans.push(serializeForRedux({ id: docSnap.id, ...docSnap.data() }));
    });

    return plans;
  } catch (error) {
    console.error("[CK-9930] Error fetching plans:", {
      errorCode: "CK-9930",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a new plan
 */
export const createNivPlan = async (planData) => {
  try {
    const plansRef = collection(db, NIV_COLLECTIONS.PLANS);
    const newDocRef = doc(plansRef);

    const now = serverTimestamp();

    const docData = {
      id: newDocRef.id,
      name: planData.name,
      code: planData.code,
      monthlyPrice: planData.monthlyPrice || 0,
      features: planData.features || [],
      isDefault: planData.isDefault || false,
      sortOrder: planData.sortOrder || 99,
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(newDocRef, docData);

    return serializeForRedux({ id: newDocRef.id, ...docData });
  } catch (error) {
    console.error("[CK-9931] Error creating plan:", {
      errorCode: "CK-9931",
      errorMessage: error.message,
      planData,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update a plan
 */
export const updateNivPlan = async (planId, data) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.PLANS, planId);

    const updateData = {
      ...data,
      updatedAt: serverTimestamp(),
    };

    // Remove undefined values
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(docRef, updateData);

    return { id: planId, ...updateData };
  } catch (error) {
    console.error("[CK-9932] Error updating plan:", {
      errorCode: "CK-9932",
      errorMessage: error.message,
      planId,
      data,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a plan
 */
export const deleteNivPlan = async (planId) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.PLANS, planId);
    await deleteDoc(docRef);

    return { id: planId };
  } catch (error) {
    console.error("[CK-9933] Error deleting plan:", {
      errorCode: "CK-9933",
      errorMessage: error.message,
      planId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Seed default plans if none exist
 * NOTE: With the new system, plans are hard-coded in SYSTEM_PLANS.
 * This function is kept for backward compatibility but no longer creates documents.
 */
export const seedDefaultPlans = async () => {
  // Plans are now hard-coded in SYSTEM_PLANS, no need to seed to database
  return { 
    seeded: false, 
    count: Object.keys(SYSTEM_PLANS).length,
    message: "Plans are now hard-coded. No database seeding needed.",
  };
};

// ============================================
// AUTO-SYNC CLINICS
// ============================================

/**
 * Sync all clinics to management collection (LEAN version)
 * Creates lean CRM-only management documents for clinics that don't have them.
 * No duplicated clinic data - only CRM-specific fields.
 */
export const syncAllClinicsToManagement = async () => {
  try {
    // Get all clinics
    const clinicsRef = collection(db, "clinics");
    const clinicsSnap = await getDocs(clinicsRef);

    // Get all existing management records
    const managementRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const managementSnap = await getDocs(managementRef);

    // Build a set of clinic IDs that already have management records
    const existingClinicRefs = new Set();
    managementSnap.forEach((docSnap) => {
      existingClinicRefs.add(docSnap.data().clinicRef);
    });

    // Create lean management records for clinics that don't have them
    const createdRecords = [];
    const now = serverTimestamp();

    for (const clinicDoc of clinicsSnap.docs) {
      const clinicId = clinicDoc.id;

      // Skip if already has management record
      if (existingClinicRefs.has(clinicId)) {
        continue;
      }

      const clinicData = clinicDoc.data();
      const planCode = clinicData.plan || "trial";
      const status = planCode === "trial" ? CLINIC_STATUSES.TRIAL : CLINIC_STATUSES.ACTIVE;

      // Create LEAN management document (CRM-only fields)
      const newDocRef = doc(collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT));

      const managementDoc = {
        id: newDocRef.id,
        clinicRef: clinicId,

        // CRM Status
        status,
        statusHistory: [{
          status,
          changedAt: Timestamp.now(),
          note: "נוצר אוטומטית מסנכרון",
        }],

        // CRM Payment tracking
        monthlyPaymentAmount: null,
        isCustomPayment: false,
        paymentMethod: null,
        paymentHistory: [],

        // CRM Notes & Logs
        internalNotes: null,
        callLogs: [],
        referralSource: null,

        // CRM Dates
        contractRenewalDate: null,
        lastContactDate: null,

        createdAt: now,
        updatedAt: now,
      };

      await setDoc(newDocRef, managementDoc);

      createdRecords.push({
        id: newDocRef.id,
        clinicId,
        clinicName: clinicData.name,
      });
    }

    return {
      synced: true,
      totalClinics: clinicsSnap.size,
      existingRecords: existingClinicRefs.size,
      newRecords: createdRecords.length,
      created: createdRecords,
    };
  } catch (error) {
    console.error("[CK-9940] Error syncing clinics:", {
      errorCode: "CK-9940",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update clinic's plan - updates BOTH the actual clinic document AND CRM management
 * This ensures the clinic.plan field (trial/starter/premium) is the source of truth
 * 
 * @param {string} clinicId - The actual clinic document ID
 * @param {string} planCode - The plan code (trial/starter/premium)
 * @param {number|null} customPayment - Optional custom payment amount
 */
export const updateClinicPlanDirectly = async (
  clinicId,
  planCode,
  customPayment = null
) => {
  try {
    const plan = SYSTEM_PLANS[planCode];
    if (!plan) {
      throw new Error(`Invalid plan code: ${planCode}`);
    }

    // 1. Update the actual clinic document
    const clinicRef = doc(db, "clinics", clinicId);
    const clinicUpdateData = {
      plan: planCode,
      updatedAt: serverTimestamp(),
    };

    // If changing FROM trial, clear trial-specific fields
    const clinicSnap = await getDoc(clinicRef);
    if (clinicSnap.exists()) {
      const currentClinic = clinicSnap.data();
      if (currentClinic.plan === "trial" && planCode !== "trial") {
        clinicUpdateData.previousPlan = "trial";
        clinicUpdateData.planChangedAt = serverTimestamp();
        clinicUpdateData.firstPaymentDate = serverTimestamp();
      }
    }

    await updateDoc(clinicRef, clinicUpdateData);

    // Legacy niv_clinic_management side-write removed — the manual CRM layer
    // (niv_clinic_crm) is admin-managed and independent of plan changes.

    return {
      clinicId, 
      planCode, 
      planName: plan.nameHebrew,
      monthlyPrice: customPayment || plan.monthlyPrice,
    };
  } catch (error) {
    console.error("[CK-9941] Error updating clinic plan:", {
      errorCode: "CK-9941",
      errorMessage: error.message,
      clinicId,
      planCode,
      customPayment,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Set (or clear) the clinic's plan end date.
 *
 * When set to a date, a nightly Cloud Function (`revertExpiredPaidPlans`) reverts the
 * clinic to an expired "trial" plan once that date passes. Passing a falsy value
 * ("not relevant") deletes the field entirely, so the cron's range query never matches
 * the clinic.
 *
 * @param {string} clinicId
 * @param {string|null} isoDate - "YYYY-MM-DD" to set, or null/"" to remove.
 */
export const setClinicPlanEndDate = async (clinicId, isoDate) => {
  try {
    const clinicRef = doc(db, "clinics", clinicId);
    if (isoDate) {
      await updateDoc(clinicRef, {
        planEndDate: Timestamp.fromDate(new Date(isoDate)),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(clinicRef, {
        planEndDate: deleteField(),
        updatedAt: serverTimestamp(),
      });
    }
    return { clinicId, planEndDate: isoDate || null };
  } catch (error) {
    console.error("[CK-9947] Error setting clinic plan end date:", {
      errorCode: "CK-9947",
      errorMessage: error.message,
      clinicId,
      isoDate,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update clinic's custom payment amount only (without changing plan)
 */
export const updateClinicCustomPayment = async (clinicId, customPayment) => {
  try {
    // Find the CRM management document
    const managementRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const managementQuery = query(managementRef, where("clinicRef", "==", clinicId));
    const managementSnap = await getDocs(managementQuery);

    if (managementSnap.empty) {
      throw new Error("CRM management document not found for clinic");
    }

    const managementDoc = managementSnap.docs[0];
    await updateDoc(managementDoc.ref, {
      monthlyPaymentAmount: customPayment,
      isCustomPayment: true,
      updatedAt: serverTimestamp(),
    });

    return { clinicId, customPayment };
  } catch (error) {
    console.error("[CK-9943] Error updating custom payment:", {
      errorCode: "CK-9943",
      errorMessage: error.message,
      clinicId,
      customPayment,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Legacy function - kept for compatibility
export const updateClinicPlan = async (
  managementId,
  planId,
  customPayment = null
) => {
  console.warn("[DEPRECATED] updateClinicPlan is deprecated. Use updateClinicPlanDirectly instead.");
  // Legacy implementation for backward compatibility
  try {
    const managementRef = doc(
      db,
      NIV_COLLECTIONS.CLINIC_MANAGEMENT,
      managementId
    );

    let updateData = {
      updatedAt: serverTimestamp(),
    };

    if (planId) {
      const planRef = doc(db, NIV_COLLECTIONS.PLANS, planId);
      const planSnap = await getDoc(planRef);

      if (planSnap.exists()) {
        const planData = planSnap.data();
        updateData.planId = planId;
        updateData.planName = planData.name;

        if (customPayment !== null) {
          updateData.monthlyPaymentAmount = customPayment;
          updateData.isCustomPayment = true;
        } else {
          updateData.monthlyPaymentAmount = planData.monthlyPrice;
          updateData.isCustomPayment = false;
        }
      }
    } else if (customPayment !== null) {
      updateData.monthlyPaymentAmount = customPayment;
      updateData.isCustomPayment = true;
    }

    await updateDoc(managementRef, updateData);

    return { id: managementId, ...updateData };
  } catch (error) {
    console.error("[CK-9941] Error updating clinic plan:", {
      errorCode: "CK-9941",
      errorMessage: error.message,
      managementId,
      planId,
      customPayment,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get total monthly revenue from all active clinics
 */
export const getTotalMonthlyRevenue = async () => {
  try {
    const managementRef = collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT);
    const activeQuery = query(
      managementRef,
      where("status", "in", [CLINIC_STATUSES.ACTIVE, CLINIC_STATUSES.TRIAL])
    );
    const managementSnap = await getDocs(activeQuery);

    let totalRevenue = 0;
    let activeCount = 0;
    let trialCount = 0;

    managementSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.status === CLINIC_STATUSES.ACTIVE) {
        totalRevenue += data.monthlyPaymentAmount || 0;
        activeCount++;
      } else if (data.status === CLINIC_STATUSES.TRIAL) {
        trialCount++;
      }
    });

    return {
      totalRevenue,
      activeCount,
      trialCount,
    };
  } catch (error) {
    console.error("[CK-9942] Error calculating revenue:", {
      errorCode: "CK-9942",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// LENNY (AI SUPPORT) — REVIEW QUEUE + CLINIC HISTORY
// ============================================

const toIsoOrNull = (ts) =>
  ts ? serializeForRedux({ date: ts }).date : null;

/**
 * Review items written by the aiSupportChat Cloud Function: exchanges Lenny
 * could not answer (flagType "unknown") or rated ≥ 80 for admin attention
 * (flagType "attention").
 * @param {{ status?: "open"|"resolved"|"all", limitCount?: number }} options
 */
export const getLennyReviews = async ({ status = "open", limitCount = 200 } = {}) => {
  try {
    const constraints = [];
    if (status && status !== "all") constraints.push(where("status", "==", status));
    constraints.push(orderBy("createdAt", "desc"), limit(limitCount));
    const snap = await getDocs(
      query(collection(db, NIV_COLLECTIONS.LENNY_REVIEWS), ...constraints)
    );
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        createdAt: toIsoOrNull(data.createdAt),
        resolvedAt: toIsoOrNull(data.resolvedAt),
      };
    });
  } catch (error) {
    console.error("[CK-8472] Error fetching Lenny reviews:", {
      errorCode: "CK-8472",
      errorMessage: error.message,
      status,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Mark a Lenny review as handled (or reopen it).
 */
export const setLennyReviewStatus = async (reviewId, { status, resolvedBy = "" }) => {
  try {
    const isResolved = status === "resolved";
    await updateDoc(doc(db, NIV_COLLECTIONS.LENNY_REVIEWS, reviewId), {
      status: isResolved ? "resolved" : "open",
      resolvedAt: isResolved ? serverTimestamp() : null,
      resolvedBy: isResolved ? resolvedBy : null,
    });
  } catch (error) {
    console.error("[CK-8473] Error updating Lenny review status:", {
      errorCode: "CK-8473",
      errorMessage: error.message,
      reviewId,
      status,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Every Lenny message across all therapists of a clinic (collection-group
 * query on therapists/{uid}/supportChat, filtered by clinicId), newest first,
 * with therapist names resolved.
 */
export const getClinicLennyHistory = async (clinicId, { limitCount = 400 } = {}) => {
  try {
    const [messagesSnap, therapistsSnap] = await Promise.all([
      getDocs(
        query(
          collectionGroup(db, "supportChat"),
          where("clinicId", "==", clinicId),
          orderBy("createdAt", "desc"),
          limit(limitCount)
        )
      ),
      getDocs(query(collection(db, "therapists"), where("clinicId", "==", clinicId))),
    ]);

    const nameByUid = {};
    therapistsSnap.forEach((d) => {
      const t = d.data();
      nameByUid[d.id] =
        t.fullName || [t.firstName, t.lastName].filter(Boolean).join(" ") || "";
    });

    return messagesSnap.docs.map((d) => {
      const data = d.data();
      const therapistId = d.ref.parent.parent?.id || null;
      return {
        id: d.id,
        therapistId,
        therapistName: (therapistId && nameByUid[therapistId]) || "",
        role: data.role === "assistant" ? "assistant" : "user",
        content: data.content || "",
        lang: data.lang || "he",
        dayKey: data.dayKey || "",
        flagged: data.flagged === true,
        covered: data.covered,
        adminAttention: data.adminAttention ?? null,
        reviewId: data.reviewId || null,
        createdAt: toIsoOrNull(data.createdAt),
      };
    });
  } catch (error) {
    console.error("[CK-8474] Error fetching clinic Lenny history:", {
      errorCode: "CK-8474",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// ADMIN MONITORING — CLINIC HEALTH CARDS (niv_alerts v2)
// One doc per clinic, recomputed daily by dailyAdminMonitoring.
// "טופל" = snooze: the card hides for N days unless the situation escalates.
// ============================================

export const ALERT_SEVERITY = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "critical",
};

export const ALERT_SEVERITY_LABELS = {
  [ALERT_SEVERITY.INFO]: "מידע",
  [ALERT_SEVERITY.WARNING]: "אזהרה",
  [ALERT_SEVERITY.CRITICAL]: "קריטי",
};

const ALERT_SEVERITY_RANK = { critical: 3, warning: 2, info: 1 };
const MONITORING_ALERT_SCHEMA_VERSION = 2;

/**
 * Get the clinic health cards that are currently ACTIVE (not snoozed).
 * The collection is small (≤ one doc per clinic), so it is read whole and
 * filtered in memory — a snooze that expired before the next cron run is
 * treated as active here too.
 * @returns {Promise<Array>} cards sorted critical → warning → info, oldest first
 */
export const getMonitoringAlerts = async () => {
  try {
    const snap = await getDocs(collection(db, NIV_COLLECTIONS.ALERTS));
    const now = Date.now();
    const cards = [];
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.v !== MONITORING_ALERT_SCHEMA_VERSION) return;
      const snoozedUntil = data.snoozedUntil
        ? new Date(serializeForRedux({ d: data.snoozedUntil }).d).getTime()
        : null;
      const isSnoozed = data.status === "snoozed" && snoozedUntil && snoozedUntil > now;
      if (isSnoozed) return;
      cards.push(
        serializeForRedux({
          id: docSnap.id,
          ...data,
        })
      );
    });
    cards.sort(
      (a, b) =>
        (ALERT_SEVERITY_RANK[b.severity] || 0) - (ALERT_SEVERITY_RANK[a.severity] || 0) ||
        String(a.firstSeenAt || "").localeCompare(String(b.firstSeenAt || ""))
    );
    return cards;
  } catch (error) {
    console.error("[CK-9960] Error fetching clinic health cards:", {
      errorCode: "CK-9960",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

const buildSnoozePatch = (card, days) => {
  const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  return {
    status: "snoozed",
    snoozedUntil: Timestamp.fromDate(until),
    snoozedAt: serverTimestamp(),
    snoozedDays: days,
    snoozedSignalKeys: card?.signalKeys || [],
    snoozedSeverity: card?.severity || null,
    snoozeCount: (card?.snoozeCount || 0) + 1,
    resurfacedReason: null,
  };
};

/**
 * Snooze a clinic health card ("טופל"). Hidden for `days` unless the cron
 * detects an escalation (higher severity / new critical signal).
 * @param {Object} card - the card as returned by getMonitoringAlerts
 * @param {number} days - 7 (default) or 30
 */
export const snoozeMonitoringAlert = async (card, days = 7) => {
  try {
    await updateDoc(doc(db, NIV_COLLECTIONS.ALERTS, card.id), buildSnoozePatch(card, days));
  } catch (error) {
    console.error("[CK-9964] Error snoozing clinic health card:", {
      errorCode: "CK-9964",
      errorMessage: error.message,
      clinicId: card?.id,
      days,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Snooze many cards at once (briefing "clear all").
 * @param {Array} cards
 * @param {number} days
 */
export const snoozeMonitoringAlertsBulk = async (cards = [], days = 7) => {
  try {
    for (let i = 0; i < cards.length; i += 450) {
      const batch = writeBatch(db);
      cards.slice(i, i + 450).forEach((card) => {
        batch.update(doc(db, NIV_COLLECTIONS.ALERTS, card.id), buildSnoozePatch(card, days));
      });
      await batch.commit();
    }
  } catch (error) {
    console.error("[CK-9869] Error bulk-snoozing clinic health cards:", {
      errorCode: "CK-9869",
      errorMessage: error.message,
      count: cards.length,
      days,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// SYSTEM NOTIFICATIONS (custom sub-specialization alerts, etc.)
// ============================================

/**
 * Get all system notifications (ordered by newest first)
 * @param {number} [limitCount=100] - Max number to fetch
 * @returns {Promise<Array>} Array of notification objects
 */
export const getSystemNotifications = async (limitCount = 100) => {
  try {
    const notifRef = collection(db, NIV_COLLECTIONS.SYSTEM_NOTIFICATIONS);
    const q = query(notifRef, orderBy("createdAt", "desc"), limit(limitCount));
    const snapshot = await getDocs(q);

    const results = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      results.push({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt
          ? serializeForRedux({ date: data.createdAt }).date
          : null,
      });
    });

    return results;
  } catch (error) {
    console.error("[CK-9975] Error fetching system notifications:", {
      errorCode: "CK-9975",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a system notification by ID
 * @param {string} notifId - The notification document ID
 */
export const deleteSystemNotification = async (notifId) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.SYSTEM_NOTIFICATIONS, notifId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("[CK-9976] Error deleting system notification:", {
      errorCode: "CK-9976",
      errorMessage: error.message,
      notifId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// SYSTEM MESSAGES (NiV broadcasts -> entry popups for all users)
// ============================================

const SYSTEM_MESSAGES_COLLECTION = "system_messages";

/**
 * Fetch all system messages (active + inactive) for the NiV admin list.
 * @returns {Promise<Array>} messages, newest first
 */
export const getNivSystemMessages = async () => {
  try {
    const messagesRef = collection(db, SYSTEM_MESSAGES_COLLECTION);
    const snapshot = await getDocs(messagesRef);

    const results = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt
          ? serializeForRedux({ date: data.createdAt }).date
          : null,
      };
    });

    results.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime; // newest first
    });

    return results;
  } catch (error) {
    console.error("[CK-9982] Error fetching system messages:", {
      errorCode: "CK-9982",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a new system message broadcast.
 * @param {{ title: string, body: string, type: string, createdBy: string }} data
 */
export const createNivSystemMessage = async ({ title, body, type, createdBy }) => {
  try {
    const messagesRef = collection(db, SYSTEM_MESSAGES_COLLECTION);
    const newRef = doc(messagesRef);
    await setDoc(newRef, {
      title: title || "",
      body: body || "",
      type: type || "info",
      isActive: true,
      createdBy: createdBy || null,
      createdAt: serverTimestamp(),
    });
    return { id: newRef.id };
  } catch (error) {
    console.error("[CK-9983] Error creating system message:", {
      errorCode: "CK-9983",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a system message (removes it for everyone).
 * @param {string} messageId
 */
export const deleteNivSystemMessage = async (messageId) => {
  try {
    const docRef = doc(db, SYSTEM_MESSAGES_COLLECTION, messageId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("[CK-9984] Error deleting system message:", {
      errorCode: "CK-9984",
      errorMessage: error.message,
      messageId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// PAGE VIEW ANALYTICS
// ============================================



// ============================================
// ERROR LOGGING
// ============================================

/**
 * Log an error to the niv error log collection (fire-and-forget)
 */
export const logErrorToNiv = async ({
  errorCode,
  errorMessage,
  clinicId,
  clinicName,
  context,
  route,
  therapistId,
  therapistName,
  patientId,
  stack,
  userAgent,
  severity,
}) => {
  try {
    const errorRef = collection(db, NIV_COLLECTIONS.ERROR_LOGS);
    await setDoc(doc(errorRef), {
      errorCode: errorCode || "UNKNOWN",
      errorMessage: errorMessage || "",
      clinicId: clinicId || null,
      clinicName: clinicName || null,
      context: context || null,
      route: route || null,
      therapistId: therapistId || null,
      therapistName: therapistName || null,
      patientId: patientId || null,
      stack: stack || null,
      userAgent: userAgent || null,
      severity: severity || "error",
      occurredAt: serverTimestamp(),
    });
  } catch {
    // Silently swallow — never re-throw or console.error with a CK- code here
    // to avoid triggering the error interceptor in an infinite loop.
  }
};

/**
 * Update an error log's status ("open" | "handled").
 */
export const updateErrorLogStatus = async (errorId, status) => {
  try {
    const docRef = doc(db, NIV_COLLECTIONS.ERROR_LOGS, errorId);
    await updateDoc(docRef, {
      status,
      handledAt: status === "handled" ? serverTimestamp() : null,
    });
    return { id: errorId, status };
  } catch (error) {
    console.error("[CK-9992] Error updating error log status:", error.message);
    throw error;
  }
};

/**
 * Permanently delete an error log doc.
 */
export const deleteErrorLog = async (errorId) => {
  try {
    await deleteDoc(doc(db, NIV_COLLECTIONS.ERROR_LOGS, errorId));
    return { id: errorId };
  } catch (error) {
    console.error("[CK-9993] Error deleting error log:", error.message);
    throw error;
  }
};

/**
 * Permanently delete ALL error log docs. Batches in chunks of 450
 * (Firestore batch limit is 500). Returns count deleted.
 */
export const deleteAllErrorLogs = async () => {
  try {
    const errorRef = collection(db, NIV_COLLECTIONS.ERROR_LOGS);
    let totalDeleted = 0;

    // Loop until collection is drained
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const snapshot = await getDocs(query(errorRef, limit(450)));
      if (snapshot.empty) break;

      const batch = writeBatch(db);
      snapshot.forEach((docSnap) => batch.delete(docSnap.ref));
      await batch.commit();
      totalDeleted += snapshot.size;

      if (snapshot.size < 450) break;
    }

    return { deleted: totalDeleted };
  } catch (error) {
    console.error("[CK-9994] Error deleting all error logs:", error.message);
    throw error;
  }
};

/**
 * Get error logs for Niv dashboard
 */
export const getErrorLogs = async (filters = {}) => {
  try {
    const { days = 7 } = filters;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const errorRef = collection(db, NIV_COLLECTIONS.ERROR_LOGS);
    const q = query(
      errorRef,
      where("occurredAt", ">=", Timestamp.fromDate(cutoffDate)),
      orderBy("occurredAt", "desc"),
      limit(500)
    );

    const snapshot = await getDocs(q);
    const results = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      results.push({
        id: docSnap.id,
        ...data,
        occurredAt: data.occurredAt?.toDate?.() || new Date(),
      });
    });

    return results;
  } catch (error) {
    console.error("[CK-9991] Error fetching error logs:", error.message);
    throw error;
  }
};

// ============================================
// REGISTRATION WHITELIST
// ============================================

export const normalizePhoneToInternational = (raw) => {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+972") && digits.length === 12) return `+${digits}`;
  if (digits.length === 10 && digits.startsWith("0")) {
    return `+972${digits.slice(1)}`;
  }
  if (digits.length === 12 && digits.startsWith("972")) {
    return `+${digits}`;
  }
  if (digits.length === 9 && digits.startsWith("5")) {
    return `+972${digits}`;
  }
  return null;
};

/**
 * Add a phone number to the registration whitelist.
 * Doc ID = international-format phone so firestore.rules can `exists()` against
 * `request.auth.token.phone_number` at clinic-create time.
 */
export const addPhoneToWhitelist = async ({ phone, note, addedBy, addedByName }) => {
  try {
    const normalized = normalizePhoneToInternational(phone);
    if (!normalized) {
      throw new Error("מספר טלפון לא תקין");
    }

    const ref = doc(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST, normalized);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      throw new Error("מספר הטלפון כבר נמצא ברשימה");
    }

    const payload = {
      phone: normalized,
      note: (note || "").trim() || null,
      addedBy: addedBy || null,
      addedByName: addedByName || null,
      addedAt: serverTimestamp(),
      usedAt: null,
    };
    await setDoc(ref, payload);

    return { id: normalized, ...payload, addedAt: new Date().toISOString() };
  } catch (error) {
    console.error("[CK-9051] Error adding phone to whitelist:", {
      errorCode: "CK-9051",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Remove a phone number from the registration whitelist.
 */
export const removePhoneFromWhitelist = async ({ phone }) => {
  try {
    const normalized = normalizePhoneToInternational(phone) || phone;
    const ref = doc(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST, normalized);
    await deleteDoc(ref);
    return { id: normalized };
  } catch (error) {
    console.error("[CK-9052] Error removing phone from whitelist:", {
      errorCode: "CK-9052",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get all whitelisted phones (sorted by addedAt desc).
 */
export const getRegistrationWhitelist = async () => {
  try {
    const ref = collection(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST);
    const q = query(ref, orderBy("addedAt", "desc"));
    const snap = await getDocs(q);
    const entries = [];
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      entries.push({
        id: docSnap.id,
        ...data,
        addedAt: data.addedAt?.toDate?.()?.toISOString() || null,
        usedAt: data.usedAt?.toDate?.()?.toISOString() || null,
      });
    });
    return entries;
  } catch (error) {
    console.error("[CK-9053] Error fetching registration whitelist:", {
      errorCode: "CK-9053",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// PROSPECTS (UNIFIED CRM PIPELINE)
// ============================================
//
// One doc per phone number (doc ID = E164, same keying as the registration
// whitelist). Tracks the full lifecycle lead -> contacted -> approved ->
// registered -> lost, with a unified notes thread that survives every stage.
// Public lead forms keep writing to niv_call_requests (public-create rule is
// untouched); getNivProspectsBoard merges those into prospects on load.

const prospectStageHistoryEntry = (stage, author) => ({
  stage,
  at: new Date().toISOString(),
  by: author || null,
});

const serializeProspect = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: serializeForRedux({ date: data.createdAt }).date,
    updatedAt: serializeForRedux({ date: data.updatedAt }).date,
    whitelistedAt: data.whitelistedAt
      ? serializeForRedux({ date: data.whitelistedAt }).date
      : null,
    registeredAt: data.registeredAt
      ? serializeForRedux({ date: data.registeredAt }).date
      : null,
    staleApprovedDismissedAt: data.staleApprovedDismissedAt
      ? serializeForRedux({ date: data.staleApprovedDismissedAt }).date
      : null,
    // notes / stageHistory entries store ISO strings already
    notes: data.notes || [],
    stageHistory: data.stageHistory || [],
    callRequestIds: data.callRequestIds || [],
    // Leads CRM (Oct 2026): single free-text notes box + AI chat summary
    freeNotes: data.freeNotes || null,
    freeNotesUpdatedAt: data.freeNotesUpdatedAt
      ? serializeForRedux({ date: data.freeNotesUpdatedAt }).date
      : null,
    nextAction: serializeNextAction(data),
    aiSummary: serializeAiSummary(data.aiSummary),
    linkedAt: data.linkedAt ? serializeForRedux({ date: data.linkedAt }).date : null,
    linkedBy: data.linkedBy || null,
    manualUnlinkedAt: data.manualUnlinkedAt
      ? serializeForRedux({ date: data.manualUnlinkedAt }).date
      : null,
  };
};

/**
 * "Next action" (Oct 2026) — one free-text box + optional reminder time on a
 * lead or a clinic CRM record. Flat fields on the doc:
 *   nextActionText       string|null
 *   nextActionAt         Timestamp|null   reminder time chosen by the admin
 *   nextActionDueAt      Timestamp        pending-push marker; the 5-minute
 *                                         scheduler (nivNextActionReminders)
 *                                         deletes it after the push fires
 *   nextActionBy         uid              who set it → push recipient
 *   nextActionUpdatedAt  Timestamp
 *   nextActionNotifiedAt Timestamp|null   when the push went out
 * → Redux-safe { text, at, pending, by, updatedAt, notifiedAt }
 */
const serializeNextAction = (data) => {
  if (!data) return null;
  const toIso = (v) => (v ? serializeForRedux({ date: v }).date : null);
  const text = data.nextActionText || null;
  const at = toIso(data.nextActionAt);
  if (!text && !at && !data.nextActionUpdatedAt) return null;
  return {
    text,
    at,
    pending: !!data.nextActionDueAt,
    by: data.nextActionBy || null,
    updatedAt: toIso(data.nextActionUpdatedAt),
    notifiedAt: toIso(data.nextActionNotifiedAt),
  };
};

/**
 * Shared validation + field builder for the next-action box. A reminder time,
 * when set, must be in the future (the scheduler would otherwise fire it on
 * the next tick). Clearing both text and time wipes every field.
 */
const buildNextActionFields = ({ text, at }) => {
  const cleanText = (text || "").trim() || null;
  let atDate = null;
  if (at) {
    atDate = at instanceof Date ? at : new Date(at);
    if (Number.isNaN(atDate.getTime())) throw new Error("מועד התזכורת אינו תקין");
    if (atDate.getTime() <= Date.now()) throw new Error("מועד התזכורת חייב להיות בעתיד");
  }
  if (!cleanText && !atDate) {
    return {
      nextActionText: deleteField(),
      nextActionAt: deleteField(),
      nextActionDueAt: deleteField(),
      nextActionBy: deleteField(),
      nextActionUpdatedAt: deleteField(),
      nextActionNotifiedAt: deleteField(),
    };
  }
  const uid = getAuth(app).currentUser?.uid || null;
  return {
    nextActionText: cleanText,
    nextActionAt: atDate ? Timestamp.fromDate(atDate) : null,
    // Only a (future) time arms the push; a text-only action has nothing to fire.
    nextActionDueAt: atDate ? Timestamp.fromDate(atDate) : deleteField(),
    nextActionBy: uid,
    nextActionUpdatedAt: serverTimestamp(),
    nextActionNotifiedAt: null,
  };
};

/** AI chat summary (written server-side by nivChatSummary) → Redux-safe shape */
const serializeAiSummary = (summary) => {
  if (!summary || typeof summary !== "object") return null;
  return {
    text: summary.text || "",
    generatedAt: summary.generatedAt
      ? serializeForRedux({ date: summary.generatedAt }).date
      : null,
    sourceFileName: summary.sourceFileName || null,
    messagesCount: summary.messagesCount ?? null,
    firstMessageAt: summary.firstMessageAt || null,
    lastMessageAt: summary.lastMessageAt || null,
    model: summary.model || null,
  };
};

// Map a call-request status to an initial prospect stage
const CALL_STATUS_TO_STAGE = {
  pending: PROSPECT_STAGES.LEAD,
  contacted: PROSPECT_STAGES.CONTACTED,
  scheduled: PROSPECT_STAGES.CONTACTED,
  completed: PROSPECT_STAGES.CONTACTED,
  cancelled: PROSPECT_STAGES.LOST,
};

/**
 * Fetch the full prospects board: prospects + call requests + whitelist +
 * therapists, then materialize any missing prospect docs (idempotent
 * merge-on-load, chunked batches). Each prospect is enriched with
 * existsInSystem / matchedTherapistUid / matchedClinicId.
 */
export const getNivProspectsBoard = async () => {
  try {
    const [prospectsSnap, callRequestsSnap, whitelistSnap, therapistsSnap] =
      await Promise.all([
        getDocs(collection(db, NIV_COLLECTIONS.PROSPECTS)),
        getDocs(query(collection(db, NIV_COLLECTIONS.CALL_REQUESTS), orderBy("createdAt", "desc"))),
        getDocs(collection(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST)),
        getDocs(collection(db, "therapists")),
      ]);

    // Therapist phone map (both +972 and legacy 05 formats normalize to E164)
    const therapistByPhone = new Map();
    therapistsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const normalized = normalizePhoneToInternational(data.phone);
      if (normalized && !therapistByPhone.has(normalized)) {
        therapistByPhone.set(normalized, {
          uid: data.uid || docSnap.id,
          clinicId: data.clinicId || null,
          fullName: data.fullName || "",
        });
      }
    });

    const prospectsMap = new Map();
    prospectsSnap.forEach((docSnap) => {
      prospectsMap.set(docSnap.id, { snap: docSnap, data: docSnap.data() });
    });

    // Pending batched writes (chunked at 450 ops)
    const batches = [];
    let currentBatch = writeBatch(db);
    let opCount = 0;
    const addOp = (fn) => {
      fn(currentBatch);
      opCount++;
      if (opCount >= 450) {
        batches.push(currentBatch);
        currentBatch = writeBatch(db);
        opCount = 0;
      }
    };

    // Track locally-materialized prospects so serialization below sees them
    const materialized = new Map(); // phone -> plain data object

    // 1. Merge call requests into prospects
    const unlinkedRequests = [];
    const callRequestsByPhone = new Map(); // e164 -> [request data]
    callRequestsSnap.forEach((docSnap) => {
      const data = { id: docSnap.id, ...docSnap.data() };
      const normalized = normalizePhoneToInternational(data.phone);
      if (!normalized) {
        unlinkedRequests.push({
          ...data,
          createdAt: serializeForRedux({ date: data.createdAt }).date,
          updatedAt: serializeForRedux({ date: data.updatedAt }).date,
          conversationLogs: (data.conversationLogs || []).map((log) => ({
            ...log,
            createdAt: serializeForRedux({ date: log.createdAt }).date,
          })),
        });
        return;
      }
      if (!callRequestsByPhone.has(normalized)) callRequestsByPhone.set(normalized, []);
      callRequestsByPhone.get(normalized).push(data);
    });

    callRequestsByPhone.forEach((requests, phone) => {
      const existing = prospectsMap.get(phone) || materialized.has(phone);
      if (!existing) {
        // Newest request wins for identity fields; stage from newest status
        const newest = requests[0];
        // Import old conversationLogs into the unified notes thread
        const importedNotes = [];
        requests.forEach((req) => {
          (req.conversationLogs || []).forEach((log) => {
            importedNotes.push({
              id: log.id || generateId(),
              text: log.text || "",
              createdAt:
                serializeForRedux({ date: log.createdAt }).date ||
                new Date().toISOString(),
              author: null,
            });
          });
          if (req.notes) {
            importedNotes.push({
              id: generateId(),
              text: req.notes,
              createdAt:
                serializeForRedux({ date: req.updatedAt }).date ||
                new Date().toISOString(),
              author: null,
            });
          }
        });
        const stage = CALL_STATUS_TO_STAGE[newest.status] || PROSPECT_STAGES.LEAD;
        const prospectData = {
          phone,
          fullName: newest.fullName || null,
          clinicName: newest.clinicName || null,
          email: newest.email || null,
          stage,
          source: newest.source || null,
          notes: importedNotes,
          callRequestIds: requests.map((r) => r.id),
          whitelistedAt: null,
          registeredAt: null,
          clinicId: null,
          therapistUid: null,
          lostReason: null,
          stageHistory: [prospectStageHistoryEntry(stage, "sync")],
          createdAt: newest.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        addOp((b) => b.set(doc(db, NIV_COLLECTIONS.PROSPECTS, phone), prospectData));
        materialized.set(phone, prospectData);
      } else if (prospectsMap.has(phone)) {
        // Link any call requests not yet recorded on the existing prospect
        const existingIds = prospectsMap.get(phone).data.callRequestIds || [];
        const missing = requests.filter((r) => !existingIds.includes(r.id));
        if (missing.length > 0) {
          addOp((b) =>
            b.update(doc(db, NIV_COLLECTIONS.PROSPECTS, phone), {
              callRequestIds: arrayUnion(...missing.map((r) => r.id)),
              updatedAt: serverTimestamp(),
            })
          );
          prospectsMap.get(phone).data.callRequestIds = [
            ...existingIds,
            ...missing.map((r) => r.id),
          ];
        }
      }
    });

    // 2. Merge whitelist entries into prospects
    whitelistSnap.forEach((docSnap) => {
      const phone = docSnap.id;
      const data = docSnap.data();
      if (prospectsMap.has(phone) || materialized.has(phone)) {
        // Ensure whitelistedAt is recorded on the prospect
        const existing = prospectsMap.get(phone);
        if (existing && !existing.data.whitelistedAt) {
          addOp((b) =>
            b.update(doc(db, NIV_COLLECTIONS.PROSPECTS, phone), {
              whitelistedAt: data.addedAt || serverTimestamp(),
              updatedAt: serverTimestamp(),
            })
          );
          existing.data.whitelistedAt = data.addedAt || null;
        } else if (materialized.has(phone)) {
          const m = materialized.get(phone);
          if (!m.whitelistedAt) {
            m.whitelistedAt = data.addedAt || null;
            if (m.stage === PROSPECT_STAGES.LEAD || m.stage === PROSPECT_STAGES.CONTACTED) {
              m.stage = PROSPECT_STAGES.APPROVED;
              m.stageHistory.push(prospectStageHistoryEntry(PROSPECT_STAGES.APPROVED, "sync"));
            }
            addOp((b) =>
              b.update(doc(db, NIV_COLLECTIONS.PROSPECTS, phone), {
                whitelistedAt: data.addedAt || serverTimestamp(),
                stage: m.stage,
                stageHistory: m.stageHistory,
                updatedAt: serverTimestamp(),
              })
            );
          }
        }
        return;
      }
      const prospectData = {
        phone,
        fullName: null,
        clinicName: data.note || null,
        email: null,
        stage: PROSPECT_STAGES.APPROVED,
        source: "whitelist_import",
        notes: data.note
          ? [{
              id: generateId(),
              text: data.note,
              createdAt:
                serializeForRedux({ date: data.addedAt }).date ||
                new Date().toISOString(),
              author: data.addedByName || null,
            }]
          : [],
        callRequestIds: [],
        whitelistedAt: data.addedAt || serverTimestamp(),
        registeredAt: null,
        clinicId: null,
        therapistUid: null,
        lostReason: null,
        stageHistory: [prospectStageHistoryEntry(PROSPECT_STAGES.APPROVED, "sync")],
        createdAt: data.addedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      addOp((b) => b.set(doc(db, NIV_COLLECTIONS.PROSPECTS, phone), prospectData));
      materialized.set(phone, prospectData);
    });

    // 3. Registered detection (self-heals if the Cloud Function trigger missed)
    const advanceToRegistered = (phone, dataObj, applyLocal) => {
      const match = therapistByPhone.get(phone);
      if (!match) return;
      if (dataObj.stage === PROSPECT_STAGES.REGISTERED || dataObj.stage === PROSPECT_STAGES.LOST) return;
      // Admin explicitly unlinked this lead from a clinic — don't re-register it
      if (dataObj.manualUnlinkedAt) return;
      const history = [
        ...(dataObj.stageHistory || []),
        prospectStageHistoryEntry(PROSPECT_STAGES.REGISTERED, "sync"),
      ];
      addOp((b) =>
        b.update(doc(db, NIV_COLLECTIONS.PROSPECTS, phone), {
          stage: PROSPECT_STAGES.REGISTERED,
          registeredAt: serverTimestamp(),
          clinicId: match.clinicId,
          therapistUid: match.uid,
          stageHistory: history,
          updatedAt: serverTimestamp(),
        })
      );
      applyLocal(history, match);
    };

    prospectsMap.forEach((entry, phone) => {
      advanceToRegistered(phone, entry.data, (history, match) => {
        entry.data.stage = PROSPECT_STAGES.REGISTERED;
        entry.data.stageHistory = history;
        entry.data.clinicId = match.clinicId;
        entry.data.therapistUid = match.uid;
        entry.data.registeredAt = entry.data.registeredAt || new Date();
      });
    });
    materialized.forEach((dataObj, phone) => {
      advanceToRegistered(phone, dataObj, (history, match) => {
        dataObj.stage = PROSPECT_STAGES.REGISTERED;
        dataObj.stageHistory = history;
        dataObj.clinicId = match.clinicId;
        dataObj.therapistUid = match.uid;
        dataObj.registeredAt = dataObj.registeredAt || new Date();
      });
    });

    // Commit pending writes
    if (opCount > 0) batches.push(currentBatch);
    for (const b of batches) {
      // eslint-disable-next-line no-await-in-loop
      await b.commit();
    }

    // Build the serialized result (existing docs + locally materialized)
    const prospects = [];
    prospectsMap.forEach((entry) => {
      prospects.push({
        ...serializeProspect(entry.snap),
        // local mutations applied during sync take precedence
        ...serializeForRedux({
          stage: entry.data.stage,
          stageHistory: entry.data.stageHistory || [],
          callRequestIds: entry.data.callRequestIds || [],
          clinicId: entry.data.clinicId || null,
          therapistUid: entry.data.therapistUid || null,
          whitelistedAt: entry.data.whitelistedAt,
          registeredAt: entry.data.registeredAt,
        }),
      });
    });
    materialized.forEach((dataObj, phone) => {
      // serverTimestamp() sentinels are only meaningful inside the batch write —
      // replace them with a client-side Date before the data reaches Redux.
      const sanitize = (v) =>
        v && typeof v === "object" && typeof v.toDate !== "function" && v.seconds === undefined && !(v instanceof Date) && !Array.isArray(v)
          ? new Date()
          : v;
      prospects.push(
        serializeForRedux({
          id: phone,
          ...dataObj,
          createdAt: sanitize(dataObj.createdAt),
          whitelistedAt: sanitize(dataObj.whitelistedAt),
          registeredAt: sanitize(dataObj.registeredAt),
          updatedAt: new Date(),
        })
      );
    });

    // Attach existsInSystem + linked call-request details
    const callRequestDetailById = new Map();
    callRequestsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      callRequestDetailById.set(docSnap.id, {
        id: docSnap.id,
        ...data,
        createdAt: serializeForRedux({ date: data.createdAt }).date,
        updatedAt: serializeForRedux({ date: data.updatedAt }).date,
        conversationLogs: (data.conversationLogs || []).map((log) => ({
          ...log,
          createdAt: serializeForRedux({ date: log.createdAt }).date,
        })),
      });
    });

    const whitelistedPhones = new Set();
    whitelistSnap.forEach((docSnap) => whitelistedPhones.add(docSnap.id));

    const enriched = prospects.map((p) => {
      const match = therapistByPhone.get(p.phone) || null;
      const linkedRequests = (p.callRequestIds || [])
        .map((id) => callRequestDetailById.get(id))
        .filter(Boolean);
      // /start extras — read-side only, never written to niv_prospects
      const extras = pickLeadExtras(linkedRequests);
      return {
        ...p,
        profession: extras.profession,
        lastMessage: extras.message,
        utm: extras.utm,
        // Live truth from /niv_registration_whitelist (whitelistedAt on the
        // prospect can be stale after a manual whitelist removal).
        isWhitelisted: whitelistedPhones.has(p.id),
        existsInSystem: !!match,
        matchedTherapistUid: match?.uid || null,
        matchedClinicId: match?.clinicId || null,
        matchedTherapistName: match?.fullName || null,
        callRequests: linkedRequests,
      };
    });

    // Newest first
    enriched.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });

    const counts = {};
    PROSPECT_STAGE_ORDER.forEach((s) => {
      counts[s] = enriched.filter((p) => p.stage === s).length;
    });

    return { prospects: enriched, unlinkedRequests, counts };
  } catch (error) {
    console.error("[CK-9057] Error fetching prospects board:", {
      errorCode: "CK-9057",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create a prospect manually (no prior call request needed).
 */
export const createProspect = async ({
  phone,
  fullName,
  clinicName,
  email,
  source,
  initialNote,
  author,
}) => {
  try {
    const normalized = normalizePhoneToInternational(phone);
    if (!normalized) {
      throw new Error("מספר טלפון לא תקין");
    }
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, normalized);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      throw new Error("כבר קיים לקוח עם מספר טלפון זה");
    }

    const prospectData = {
      phone: normalized,
      fullName: (fullName || "").trim() || null,
      clinicName: (clinicName || "").trim() || null,
      email: (email || "").trim().toLowerCase() || null,
      stage: PROSPECT_STAGES.LEAD,
      source: source || "manual",
      notes: (initialNote || "").trim()
        ? [{
            id: generateId(),
            text: initialNote.trim(),
            createdAt: new Date().toISOString(),
            author: author || null,
          }]
        : [],
      callRequestIds: [],
      whitelistedAt: null,
      registeredAt: null,
      clinicId: null,
      therapistUid: null,
      lostReason: null,
      stageHistory: [prospectStageHistoryEntry(PROSPECT_STAGES.LEAD, author)],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(ref, prospectData);
    return serializeForRedux({
      id: normalized,
      ...prospectData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error("[CK-9058] Error creating prospect:", {
      errorCode: "CK-9058",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update prospect identity/details fields (fullName, clinicName, email, source).
 */
export const updateProspectDetails = async ({ phone, updates }) => {
  try {
    const allowed = {};
    ["fullName", "clinicName", "email", "source"].forEach((key) => {
      if (updates[key] !== undefined) allowed[key] = updates[key];
    });
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    await updateDoc(ref, { ...allowed, updatedAt: serverTimestamp() });
    return { id: phone, ...allowed };
  } catch (error) {
    console.error("[CK-9059] Error updating prospect details:", {
      errorCode: "CK-9059",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Dismiss the "approved but never registered" briefing alert for a prospect.
 * The prospect stays in the approved stage; only the morning-briefing nag
 * (and the owner digest count) stop showing it.
 */
export const dismissStaleApprovedProspect = async ({ phone, author }) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    await updateDoc(ref, {
      staleApprovedDismissedAt: serverTimestamp(),
      staleApprovedDismissedBy: author || null,
      updatedAt: serverTimestamp(),
    });
    return { id: phone };
  } catch (error) {
    console.error("[CK-9075] Error dismissing stale approved prospect:", {
      errorCode: "CK-9075",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Move a prospect to a different pipeline stage.
 */
export const updateProspectStage = async ({ phone, stage, author, lostReason }) => {
  try {
    if (!PROSPECT_STAGE_ORDER.includes(stage)) {
      throw new Error("שלב לא תקין");
    }
    // "approved" always means "phone is on the registration whitelist" — route
    // through approveProspectRegistration so the whitelist doc is actually
    // written (signup rules exists() against it). Stage-only writes left leads
    // marked approved while registration stayed blocked.
    if (stage === PROSPECT_STAGES.APPROVED) {
      return approveProspectRegistration({ phone, addedByName: author });
    }
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    if (stage === PROSPECT_STAGES.REGISTERED) {
      // "registered" always means "linked to a clinic" — use linkProspectToClinic
      const snap = await getDoc(ref);
      if (!snap.exists() || !snap.data().clinicId) {
        throw new Error("כדי לסמן כנרשם יש לקשר את הליד לקליניקה קיימת");
      }
    }
    const updateData = {
      stage,
      stageHistory: arrayUnion(prospectStageHistoryEntry(stage, author)),
      updatedAt: serverTimestamp(),
    };
    if (stage === PROSPECT_STAGES.LOST) {
      updateData.lostReason = (lostReason || "").trim() || null;
    }
    await updateDoc(ref, updateData);
    return { id: phone, stage };
  } catch (error) {
    console.error("[CK-9060] Error updating prospect stage:", {
      errorCode: "CK-9060",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Append a note to the prospect's unified notes thread.
 */
export const addProspectNote = async ({ phone, text, author }) => {
  try {
    const note = {
      id: generateId(),
      text: (text || "").trim(),
      createdAt: new Date().toISOString(),
      author: author || null,
    };
    if (!note.text) throw new Error("לא ניתן לשמור הערה ריקה");
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    await updateDoc(ref, {
      notes: arrayUnion(note),
      updatedAt: serverTimestamp(),
    });
    return { id: phone, note };
  } catch (error) {
    console.error("[CK-9061] Error adding prospect note:", {
      errorCode: "CK-9061",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Approve a prospect for registration: atomically writes the whitelist doc
 * (exact legacy payload shape — signup rules depend on it) and advances the
 * prospect to "approved". Idempotent if the phone is already whitelisted.
 */
export const approveProspectRegistration = async ({ phone, note, addedBy, addedByName }) => {
  try {
    const normalized = normalizePhoneToInternational(phone);
    if (!normalized) {
      throw new Error("מספר טלפון לא תקין");
    }

    const whitelistRef = doc(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST, normalized);
    const prospectRef = doc(db, NIV_COLLECTIONS.PROSPECTS, normalized);
    const alreadyWhitelisted = (await getDoc(whitelistRef)).exists();

    const batch = writeBatch(db);
    if (!alreadyWhitelisted) {
      // Exact same payload shape as addPhoneToWhitelist — do not change
      batch.set(whitelistRef, {
        phone: normalized,
        note: (note || "").trim() || null,
        addedBy: addedBy || null,
        addedByName: addedByName || null,
        addedAt: serverTimestamp(),
        usedAt: null,
      });
    }
    // set+merge so approving a phone with no prospect doc yet cannot throw
    batch.set(
      prospectRef,
      {
        phone: normalized,
        stage: PROSPECT_STAGES.APPROVED,
        whitelistedAt: serverTimestamp(),
        stageHistory: arrayUnion(prospectStageHistoryEntry(PROSPECT_STAGES.APPROVED, addedByName)),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    await batch.commit();

    return { id: normalized, stage: PROSPECT_STAGES.APPROVED, alreadyWhitelisted };
  } catch (error) {
    console.error("[CK-9062] Error approving prospect registration:", {
      errorCode: "CK-9062",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Revoke a prospect's registration approval: deletes the whitelist doc and,
 * if the prospect is currently "approved", moves it back to "contacted".
 * Idempotent if the phone is not whitelisted.
 */
export const revokeProspectRegistration = async ({ phone, author }) => {
  try {
    const normalized = normalizePhoneToInternational(phone) || phone;
    const whitelistRef = doc(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST, normalized);
    const prospectRef = doc(db, NIV_COLLECTIONS.PROSPECTS, normalized);
    const prospectSnap = await getDoc(prospectRef);

    const batch = writeBatch(db);
    batch.delete(whitelistRef);
    if (prospectSnap.exists()) {
      const updates = { whitelistedAt: null, updatedAt: serverTimestamp() };
      if (prospectSnap.data().stage === PROSPECT_STAGES.APPROVED) {
        updates.stage = PROSPECT_STAGES.CONTACTED;
        updates.stageHistory = arrayUnion(
          prospectStageHistoryEntry(PROSPECT_STAGES.CONTACTED, author)
        );
      }
      batch.update(prospectRef, updates);
    }
    await batch.commit();
    return { id: normalized, isWhitelisted: false };
  } catch (error) {
    console.error("[CK-9077] Error revoking prospect registration:", {
      errorCode: "CK-9077",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Manually link a lead to an existing clinic: sets clinicId/therapistUid and
 * advances the stage to "registered". If the lead already has an AI chat
 * summary it is copied onto the clinic's CRM record so the clinic page shows
 * it too (the lead keeps its own copy). Error code CK-9092.
 */
export const linkProspectToClinic = async ({ phone, clinicId, therapistUid, author }) => {
  try {
    if (!clinicId) throw new Error("יש לבחור קליניקה");
    const prospectRef = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    const snap = await getDoc(prospectRef);
    if (!snap.exists()) throw new Error("הליד לא נמצא");
    const data = snap.data();

    const batch = writeBatch(db);
    const prospectUpdate = {
      clinicId,
      therapistUid: therapistUid || null,
      stage: PROSPECT_STAGES.REGISTERED,
      registeredAt: data.registeredAt || serverTimestamp(),
      linkedBy: author || null,
      linkedAt: serverTimestamp(),
      manualUnlinkedAt: null,
      stageHistory: arrayUnion(prospectStageHistoryEntry(PROSPECT_STAGES.REGISTERED, author)),
      updatedAt: serverTimestamp(),
    };
    batch.update(prospectRef, prospectUpdate);

    if (data.aiSummary?.text) {
      batch.set(
        doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
        {
          clinicId,
          aiSummary: { ...data.aiSummary, copiedFromProspect: phone },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
    await batch.commit();
    return { id: phone, clinicId, stage: PROSPECT_STAGES.REGISTERED };
  } catch (error) {
    console.error("[CK-9092] Error linking prospect to clinic:", {
      errorCode: "CK-9092",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Detach a lead from its clinic. Stage falls back to "approved" when the phone
 * was whitelisted, otherwise "contacted". manualUnlinkedAt stops the board's
 * phone-match self-heal from re-registering it on the next load. CK-9093.
 */
export const unlinkProspectFromClinic = async ({ phone, author }) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("הליד לא נמצא");
    const data = snap.data();
    const nextStage = data.whitelistedAt ? PROSPECT_STAGES.APPROVED : PROSPECT_STAGES.CONTACTED;
    await updateDoc(ref, {
      clinicId: null,
      therapistUid: null,
      registeredAt: null,
      linkedBy: null,
      linkedAt: null,
      manualUnlinkedAt: serverTimestamp(),
      stage: nextStage,
      stageHistory: arrayUnion(prospectStageHistoryEntry(nextStage, author)),
      updatedAt: serverTimestamp(),
    });
    return { id: phone, stage: nextStage };
  } catch (error) {
    console.error("[CK-9093] Error unlinking prospect from clinic:", {
      errorCode: "CK-9093",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/** Single free-text notes box on a lead (autosaved from the lead page). CK-9094. */
export const updateProspectFreeNotes = async ({ phone, text }) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    await updateDoc(ref, {
      freeNotes: (text || "").trim() || null,
      freeNotesUpdatedAt: serverTimestamp(),
      // deliberately NOT bumping updatedAt — it drives "last activity" sorting
    });
    return { id: phone, freeNotes: (text || "").trim() || null };
  } catch (error) {
    console.error("[CK-9094] Error saving prospect free notes:", {
      errorCode: "CK-9094",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Next action on a lead: free text + optional future reminder time that pushes
 * the admin who set it (nivNextActionReminders). CK-9097.
 * @param {{ phone: string, text: string, at: string|Date|null }} payload
 */
export const updateProspectNextAction = async ({ phone, text, at }) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.PROSPECTS, phone);
    await updateDoc(ref, buildNextActionFields({ text, at }));
    return { id: phone };
  } catch (error) {
    console.error("[CK-9097] Error saving prospect next action:", {
      errorCode: "CK-9097",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// CLINIC CRM (MANUAL ADMIN LAYER)
// ============================================
//
// niv_clinic_crm/{clinicId} — thin manual layer connected to real clinic data:
// paymentStatus, billingCycle, amount, payments[], notes[] (Oct 2026 model).
// Paid-until is NOT here — it is the clinic's real planEndDate field.
// Legacy niv_clinic_management is no longer written; its internalNotes are
// migrated once into the new notes thread (guarded by the _meta marker doc).

const CRM_META_DOC_ID = "_meta";

const CRM_PAYMENT_STATUSES = new Set(["not_paying", "paying", "left"]);
const CRM_BILLING_CYCLES = new Set(["monthly", "one_time"]);

/** Legacy docs (pre Oct 2026) only had isPaying; map to the manual status. */
const resolvePaymentStatus = (data) => {
  if (CRM_PAYMENT_STATUSES.has(data.paymentStatus)) return data.paymentStatus;
  return data.isPaying === true ? "paying" : "not_paying";
};

const serializeClinicCrm = (data) => {
  if (!data) return null;
  const paymentStatus = resolvePaymentStatus(data);
  return {
    // Manual payment status (Oct 2026): not_paying | paying | left.
    // isPaying is a derived mirror kept for readers that still check the boolean.
    paymentStatus,
    isPaying: paymentStatus === "paying",
    leftAt: data.leftAt ? serializeForRedux({ date: data.leftAt }).date : null,
    // Cycle: monthly | one_time (legacy yearly/custom → null, data re-entered by admin)
    billingCycle: CRM_BILLING_CYCLES.has(data.billingCycle) ? data.billingCycle : null,
    amount: data.amount ?? null,
    // Legacy records without the flag are treated as VAT-inclusive
    vatIncluded: data.vatIncluded !== false,
    // Canonical pre-VAT figure — every NiV surface displays this one
    amountBeforeVat: data.amountBeforeVat ?? null,
    // One-time deals: start date ("YYYY-MM-DD") — MRR spreads the amount from
    // here until clinic.planEndDate ("פעיל עד")
    oneTimeStartDate: data.oneTimeStartDate || null,
    // Seats included in the deal (per-deal pricing). null = not configured →
    // seat-mismatch alerting stays off for this clinic.
    paidTherapistsCount: data.paidTherapistsCount ?? null,
    seatsNote: data.seatsNote || null,
    // Standing order (הו"ק באשראי) — chargeDay = day of month the admin
    // verifies the charge in the payment system (NOT planEndDate).
    isStandingOrder: data.isStandingOrder === true,
    chargeDay: data.chargeDay ?? null,
    standingOrderSince: data.standingOrderSince || null,
    skippedMonths: Array.isArray(data.skippedMonths) ? data.skippedMonths : [],
    standingOrderFailures:
      data.standingOrderFailures && typeof data.standingOrderFailures === "object"
        ? serializeForRedux(data.standingOrderFailures)
        : {},
    freeNotes: data.freeNotes || null,
    freeNotesUpdatedAt: data.freeNotesUpdatedAt
      ? serializeForRedux({ date: data.freeNotesUpdatedAt }).date
      : null,
    nextAction: serializeNextAction(data),
    aiSummary: serializeAiSummary(data.aiSummary),
    payments: (data.payments || []).map((p) => serializeForRedux(p)),
    notes: (data.notes || []).map((n) => serializeForRedux(n)),
    // Tags (global registry ids) + contact tracking (NiV clinics tab)
    tagIds: Array.isArray(data.tagIds) ? data.tagIds : [],
    contacts: (data.contacts || []).map((c) => serializeForRedux(c)),
    lastContactAt: data.lastContactAt
      ? serializeForRedux({ date: data.lastContactAt }).date
      : null,
    contactDismissedKey: data.contactDismissedKey || null,
    contactDismissedAt: data.contactDismissedAt
      ? serializeForRedux({ date: data.contactDismissedAt }).date
      : null,
  };
};

const EMPTY_CLINIC_CRM = {
  paymentStatus: "not_paying",
  isPaying: false,
  leftAt: null,
  billingCycle: null,
  amount: null,
  vatIncluded: true,
  amountBeforeVat: null,
  oneTimeStartDate: null,
  paidTherapistsCount: null,
  seatsNote: null,
  isStandingOrder: false,
  chargeDay: null,
  standingOrderSince: null,
  skippedMonths: [],
  standingOrderFailures: {},
  freeNotes: null,
  freeNotesUpdatedAt: null,
  aiSummary: null,
  payments: [],
  notes: [],
  tagIds: [],
  contacts: [],
  lastContactAt: null,
  contactDismissedKey: null,
  contactDismissedAt: null,
};

/**
 * Fetch the clinics overview: all clinics + owner therapists + manual CRM
 * layer + patient counts, with derived-friendly real fields (incl. planEndDate).
 * Runs the one-time legacy internalNotes migration if not done yet.
 */
export const getNivClinicsOverview = async () => {
  try {
    const [clinicsSnap, therapistsSnap, crmSnap] = await Promise.all([
      getDocs(collection(db, "clinics")),
      getDocs(collection(db, "therapists")),
      getDocs(collection(db, NIV_COLLECTIONS.CLINIC_CRM)),
    ]);

    const therapistsMap = {};
    // Real therapist counts per clinic, derived from /therapists docs — the
    // clinic.therapists[] array never includes the owner and drifts on
    // deactivation, so it undercounts.
    const therapistCountByClinic = {};
    therapistsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const uid = data.uid || docSnap.id;
      therapistsMap[uid] = {
        fullName: data.fullName || "",
        phone: data.phone || "",
        email: data.email || "",
        onboardingCompleted: data.onboardingCompleted === true,
      };
      if (data.clinicId) {
        const counts = therapistCountByClinic[data.clinicId] || { total: 0, active: 0 };
        counts.total++;
        if (data.isActive !== false) counts.active++;
        therapistCountByClinic[data.clinicId] = counts;
      }
    });

    const crmMap = {};
    let legacyMigrationDone = false;
    crmSnap.forEach((docSnap) => {
      if (docSnap.id === CRM_META_DOC_ID) {
        legacyMigrationDone = docSnap.data().legacyMigrationDone === true;
        return;
      }
      crmMap[docSnap.id] = docSnap.data();
    });

    // One-time legacy internalNotes migration (idempotent, marker-guarded)
    if (!legacyMigrationDone) {
      try {
        const legacySnap = await getDocs(collection(db, NIV_COLLECTIONS.CLINIC_MANAGEMENT));
        const batch = writeBatch(db);
        let migrated = 0;
        legacySnap.forEach((docSnap) => {
          const legacy = docSnap.data();
          const clinicId = legacy.clinicRef;
          if (!clinicId) return;
          const note = (legacy.internalNotes || "").trim();
          const existing = crmMap[clinicId];
          if (existing?.legacyNotesMigrated) return;
          if (!note) {
            // Nothing to migrate for this clinic; the global _meta marker
            // stops the whole pass from ever running again
            return;
          }
          const seedNote = {
            id: generateId(),
            text: note,
            createdAt:
              serializeForRedux({ date: legacy.updatedAt }).date ||
              new Date().toISOString(),
            author: "מערכת (הועבר)",
          };
          batch.set(
            doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
            {
              clinicId,
              notes: arrayUnion(seedNote),
              legacyNotesMigrated: true,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          // reflect locally for this render
          crmMap[clinicId] = {
            ...(existing || {}),
            clinicId,
            notes: [...(existing?.notes || []), seedNote],
            legacyNotesMigrated: true,
          };
          migrated++;
        });
        batch.set(
          doc(db, NIV_COLLECTIONS.CLINIC_CRM, CRM_META_DOC_ID),
          { legacyMigrationDone: true, migratedAt: serverTimestamp(), migratedCount: migrated },
          { merge: true }
        );
        await batch.commit();
      } catch (migrationError) {
        // Migration failure must not block the overview
        console.error("[CK-9069] Legacy CRM notes migration failed:", {
          errorCode: "CK-9069",
          errorMessage: migrationError.message,
          timestamp: new Date().toISOString(),
        });
      }
    }

    // Patient counts per clinic (parallel subcollection reads)
    const patientCountPromises = clinicsSnap.docs.map(async (clinicDoc) => {
      try {
        const patientsSnap = await getDocs(collection(db, "clinics", clinicDoc.id, "patients"));
        let activeCount = 0;
        patientsSnap.forEach((pDoc) => {
          if (pDoc.data().isActive !== false) activeCount++;
        });
        return { clinicId: clinicDoc.id, total: patientsSnap.size, active: activeCount };
      } catch {
        return { clinicId: clinicDoc.id, total: 0, active: 0 };
      }
    });
    const patientCounts = await Promise.all(patientCountPromises);
    const patientCountMap = {};
    patientCounts.forEach((pc) => {
      patientCountMap[pc.clinicId] = { total: pc.total, active: pc.active };
    });

    const rows = clinicsSnap.docs.map((clinicDoc) => {
      const clinicData = clinicDoc.data();
      const clinicId = clinicDoc.id;
      const owner = therapistsMap[clinicData.ownerTherapistId] || {};
      const patients = patientCountMap[clinicId] || { total: 0, active: 0 };
      const planCode = clinicData.plan || "trial";
      const planDetails = SYSTEM_PLANS[planCode] || SYSTEM_PLANS.trial;
      const crm = serializeClinicCrm(crmMap[clinicId]) || { ...EMPTY_CLINIC_CRM };

      // Pre-VAT monthly contribution of a monthly payer; one-time deals are
      // spread client-side (nivDerived.normalizeMonthlyAmount needs planEndDate)
      const monthlyNormalizedAmount =
        crm.paymentStatus === "paying" && crm.billingCycle === "monthly"
          ? Number(crm.amountBeforeVat) || 0
          : 0;

      return serializeForRedux({
        clinicId,
        // Real clinic fields (source of truth)
        name: clinicData.name || "",
        logoUrl: clinicData.logoUrl || null,
        type: clinicData.type || "",
        plan: planCode,
        planName: planDetails.nameHebrew,
        isSoloClinic: clinicData.isSoloClinic || false,
        isActive: clinicData.isActive !== false,
        trialEndDate: clinicData.trialEndDate,
        trialStartDate: clinicData.trialStartDate,
        planEndDate: clinicData.planEndDate || null,
        createdAt: clinicData.createdAt,
        updatedAt: clinicData.updatedAt,
        isMorningIntegrated: clinicData.isMorningPaymentSystemIntegrated || false,
        morningBusinessType: clinicData.morningIntegration?.businessType || null,
        morningExempt: clinicData.morningIntegration?.exemption === true || clinicData.morningIntegration?.businessType === "osekPatur",
        morningDefaultDocumentType: clinicData.morningIntegration?.defaultDocumentType ?? null,
        morningSettingsVersion: clinicData.morningIntegration?.settingsVersion ?? 1,
        isGrowIntegrated: clinicData.isGrowIntegrated === true,
        growEnvironment: clinicData.growIntegration?.environment || null,
        therapistsCount:
          therapistCountByClinic[clinicId]?.total ||
          (clinicData.therapists?.length || 0) + 1,
        activeTherapistsCount:
          therapistCountByClinic[clinicId]?.active ||
          (clinicData.therapists?.length || 0) + 1,
        // Churn markers written by the revertExpiredPaidPlans cron
        previousPaidPlan: clinicData.previousPaidPlan || null,
        planRevertedAt: clinicData.planRevertedAt || null,
        starred: clinicData.starred || false,
        inactiveByAdmin: clinicData.inactiveByAdmin === true,
        inactiveByAdminAt: clinicData.inactiveByAdminAt || null,
        // Owner therapist
        ownerName: owner.fullName || "",
        ownerPhone: owner.phone || "",
        ownerEmail: owner.email || "",
        ownerOnboardingCompleted: owner.onboardingCompleted === true,
        // Patients
        totalPatients: patients.total,
        activePatients: patients.active,
        // Manual CRM layer
        crm,
        monthlyNormalizedAmount,
      });
    });

    // Newest first
    rows.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });

    return rows;
  } catch (error) {
    console.error("[CK-9064] Error fetching clinics overview:", {
      errorCode: "CK-9064",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Update the manual CRM fields for a clinic (upsert).
 */
export const updateNivClinicCrm = async (clinicId, updates) => {
  try {
    const allowed = {};
    [
      "paymentStatus",
      "leftAt",
      "billingCycle",
      "amount",
      "vatIncluded",
      "amountBeforeVat",
      "oneTimeStartDate",
      "paidTherapistsCount",
      "seatsNote",
      "isStandingOrder",
      "chargeDay",
      "standingOrderSince",
    ].forEach((key) => {
      if (updates[key] !== undefined) allowed[key] = updates[key];
    });
    if (allowed.paymentStatus !== undefined) {
      if (!CRM_PAYMENT_STATUSES.has(allowed.paymentStatus)) {
        throw new Error("סטטוס תשלום לא תקין");
      }
      // Stored mirror for server readers (telemetry snapshot / owner digest)
      allowed.isPaying = allowed.paymentStatus === "paying";
    }
    if (allowed.billingCycle !== undefined && allowed.billingCycle !== null) {
      if (!CRM_BILLING_CYCLES.has(allowed.billingCycle)) {
        throw new Error("מחזור חיוב לא תקין");
      }
    }
    if (allowed.isStandingOrder === true) {
      const day = Number(allowed.chargeDay);
      if (!Number.isInteger(day) || day < 1 || day > 28) {
        throw new Error('יש לבחור יום חיוב בחודש (1–28) עבור הו"ק');
      }
    }
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      { clinicId, ...allowed, updatedAt: serverTimestamp() },
      { merge: true }
    );
    return { clinicId, ...allowed };
  } catch (error) {
    console.error("[CK-9065] Error updating clinic CRM:", {
      errorCode: "CK-9065",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Add a payment entry to the clinic's manual payment log.
 */
export const addNivCrmPayment = async (
  clinicId,
  { date, amount, note, month, method, amountBeforeVat }
) => {
  try {
    const payment = {
      id: generateId(),
      date: date || new Date().toISOString(),
      amount: Number(amount) || 0,
      note: (note || "").trim() || null,
      // Optional (Oct 2026): billing month "YYYY-MM", method, pre-VAT amount
      ...(month ? { month } : {}),
      ...(method ? { method } : {}),
      ...(amountBeforeVat != null ? { amountBeforeVat: Number(amountBeforeVat) || 0 } : {}),
    };
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      { clinicId, payments: arrayUnion(payment), updatedAt: serverTimestamp() },
      { merge: true }
    );
    return { clinicId, payment };
  } catch (error) {
    console.error("[CK-9066] Error adding CRM payment:", {
      errorCode: "CK-9066",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Log a payment and (optionally) extend the clinic's real planEndDate in one
 * flow. The payment is committed first — it is the money source of truth —
 * and a planEndDate failure never throws afterwards; the caller gets
 * { planEndDateError: true } and shows a "update the date manually" warning.
 *
 * @param {string} clinicId
 * @param {{date, amount, note}} payment
 * @param {string|null} newPlanEndDate - "YYYY-MM-DD" to set, or null to skip.
 */
export const addNivCrmPaymentAndExtend = async (clinicId, payment, newPlanEndDate) => {
  const result = await addNivCrmPayment(clinicId, payment);
  if (!newPlanEndDate) {
    return { ...result, planEndDateUpdated: false, planEndDateError: false };
  }
  try {
    await setClinicPlanEndDate(clinicId, newPlanEndDate);
    return {
      ...result,
      planEndDateUpdated: true,
      planEndDateError: false,
      planEndDate: newPlanEndDate,
    };
  } catch (error) {
    console.error("[CK-9863] Payment saved but planEndDate update failed:", {
      errorCode: "CK-9863",
      errorMessage: error.message,
      clinicId,
      newPlanEndDate,
      timestamp: new Date().toISOString(),
    });
    return { ...result, planEndDateUpdated: false, planEndDateError: true };
  }
};

/**
 * Standing-order month bookkeeping from the morning briefing:
 *  - "skip": month goes to skippedMonths (card disappears, no payment logged)
 *  - "fail": standingOrderFailures[month] = now (card stays, shows a red badge)
 * CK-9096.
 */
export const setStandingOrderMonthStatus = async (clinicId, { month, action }) => {
  try {
    if (!/^\d{4}-\d{2}$/.test(month || "")) throw new Error("חודש לא תקין");
    const ref = doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId);
    if (action === "skip") {
      await setDoc(
        ref,
        { clinicId, skippedMonths: arrayUnion(month), updatedAt: serverTimestamp() },
        { merge: true }
      );
    } else if (action === "fail") {
      await setDoc(
        ref,
        {
          clinicId,
          standingOrderFailures: { [month]: new Date().toISOString() },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } else {
      throw new Error("פעולה לא תקינה");
    }
    return { clinicId, month, action };
  } catch (error) {
    console.error("[CK-9096] Error updating standing-order month status:", {
      errorCode: "CK-9096",
      errorMessage: error.message,
      clinicId,
      month,
      action,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/** Single free-text notes box on a clinic CRM record. CK-9095. */
export const updateNivCrmFreeNotes = async (clinicId, text) => {
  try {
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      {
        clinicId,
        freeNotes: (text || "").trim() || null,
        freeNotesUpdatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { clinicId, freeNotes: (text || "").trim() || null };
  } catch (error) {
    console.error("[CK-9095] Error saving clinic CRM free notes:", {
      errorCode: "CK-9095",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Next action on a clinic CRM record: free text + optional future reminder
 * time that pushes the admin who set it (nivNextActionReminders). CK-9098.
 */
export const updateNivCrmNextAction = async (clinicId, { text, at }) => {
  try {
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      { clinicId, ...buildNextActionFields({ text, at }) },
      { merge: true }
    );
    return { clinicId };
  } catch (error) {
    console.error("[CK-9098] Error saving clinic CRM next action:", {
      errorCode: "CK-9098",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a payment entry from the clinic's manual payment log.
 */
export const deleteNivCrmPayment = async (clinicId, paymentId) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { clinicId, paymentId };
    const payments = (snap.data().payments || []).filter((p) => p.id !== paymentId);
    await updateDoc(ref, { payments, updatedAt: serverTimestamp() });
    return { clinicId, paymentId };
  } catch (error) {
    console.error("[CK-9067] Error deleting CRM payment:", {
      errorCode: "CK-9067",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Append a note to the clinic's CRM notes thread.
 */
export const addNivCrmNote = async (clinicId, { text, author }) => {
  try {
    const note = {
      id: generateId(),
      text: (text || "").trim(),
      createdAt: new Date().toISOString(),
      author: author || null,
    };
    if (!note.text) throw new Error("לא ניתן לשמור הערה ריקה");
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      { clinicId, notes: arrayUnion(note), updatedAt: serverTimestamp() },
      { merge: true }
    );
    return { clinicId, note };
  } catch (error) {
    console.error("[CK-9068] Error adding CRM note:", {
      errorCode: "CK-9068",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// CLINIC TAGS REGISTRY + CONTACT TRACKING
// (NiV clinics tab) — Error codes: CK-9080 - CK-9089
// ============================================
//
// niv_settings/clinicTags — global tag registry:
//   { tags: [{ id, label, color, order, createdAt }], starredMigrationDone, ... }
// niv_clinic_crm/{clinicId}.tagIds[] — assignment (full array rewrite).
// niv_clinic_crm/{clinicId}.contacts[] / lastContactAt / contactDismissedKey
//   — manual contact log + one-click dismissal of a "needs contact" trigger.

const CLINIC_TAGS_DOC_ID = "clinicTags";
export const CLINIC_TAG_COLOR_KEYS = [
  "mint",
  "blue",
  "violet",
  "amber",
  "rose",
  "teal",
  "slate",
  "orange",
  "pink",
  "indigo",
];
const FAVORITE_TAG = { id: "favorite", label: "מועדף", color: "amber" };
const BATCH_CHUNK = 450;

const serializeTag = (t, index) => ({
  id: t.id,
  label: t.label || "",
  color: CLINIC_TAG_COLOR_KEYS.includes(t.color) ? t.color : "slate",
  order: typeof t.order === "number" ? t.order : index,
  createdAt: t.createdAt ? serializeForRedux({ date: t.createdAt }).date : null,
});

/**
 * One-time migration: every clinic with `starred: true` gets the default
 * "מועדף" tag. Idempotent via the registry marker; the legacy `starred`
 * field is left untouched (ignored by the UI). Never throws — the caller
 * treats a failure as "not migrated yet" and retries next load.
 */
const runStarredTagMigration = async (registryRef, tags) => {
  try {
    const starredSnap = await getDocs(
      query(collection(db, "clinics"), where("starred", "==", true))
    );
    let nextTags = tags;
    if (starredSnap.size > 0 && !tags.some((t) => t.id === FAVORITE_TAG.id)) {
      nextTags = [
        ...tags,
        { ...FAVORITE_TAG, order: tags.length, createdAt: new Date().toISOString() },
      ];
    }
    const ids = starredSnap.docs.map((d) => d.id);
    for (let i = 0; i < ids.length; i += BATCH_CHUNK) {
      const batch = writeBatch(db);
      ids.slice(i, i + BATCH_CHUNK).forEach((clinicId) => {
        batch.set(
          doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
          { clinicId, tagIds: arrayUnion(FAVORITE_TAG.id), updatedAt: serverTimestamp() },
          { merge: true }
        );
      });
      await batch.commit();
    }
    await setDoc(
      registryRef,
      {
        tags: nextTags,
        starredMigrationDone: true,
        starredMigratedAt: serverTimestamp(),
        starredMigratedCount: ids.length,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { tags: nextTags, migrated: ids.length };
  } catch (error) {
    console.error("[CK-9081] Starred → tag migration failed:", {
      errorCode: "CK-9081",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    return { tags, migrated: 0 };
  }
};

/**
 * Fetch the global clinic tag registry (creating it on first use) and run
 * the starred→tag migration once. Returns { tags, migrated }.
 */
export const getNivClinicTagsRegistry = async () => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.SETTINGS, CLINIC_TAGS_DOC_ID);
    const snap = await getDoc(ref);
    let tags = [];
    let migrationDone = false;
    if (snap.exists()) {
      const data = snap.data();
      tags = Array.isArray(data.tags) ? data.tags : [];
      migrationDone = data.starredMigrationDone === true;
    } else {
      await setDoc(ref, { tags: [], createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
    let migrated = 0;
    if (!migrationDone) {
      const result = await runStarredTagMigration(ref, tags);
      tags = result.tags;
      migrated = result.migrated;
    }
    const sorted = tags
      .map(serializeTag)
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, "he"));
    return { tags: sorted, migrated };
  } catch (error) {
    console.error("[CK-9080] Error fetching clinic tags registry:", {
      errorCode: "CK-9080",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Create (no id) or update (id) a tag in the registry. Returns the saved tag.
 */
export const saveNivClinicTag = async ({ id, label, color }) => {
  try {
    const cleanLabel = (label || "").trim();
    if (!cleanLabel) throw new Error("שם תגית ריק");
    if (!CLINIC_TAG_COLOR_KEYS.includes(color)) throw new Error("צבע תגית לא חוקי");
    const ref = doc(db, NIV_COLLECTIONS.SETTINGS, CLINIC_TAGS_DOC_ID);
    const snap = await getDoc(ref);
    const tags = snap.exists() && Array.isArray(snap.data().tags) ? snap.data().tags : [];
    let saved;
    let nextTags;
    if (id) {
      const idx = tags.findIndex((t) => t.id === id);
      if (idx === -1) throw new Error("התגית לא נמצאה");
      saved = { ...tags[idx], label: cleanLabel, color };
      nextTags = tags.map((t, i) => (i === idx ? saved : t));
    } else {
      saved = {
        id: generateId(),
        label: cleanLabel,
        color,
        order: tags.length,
        createdAt: new Date().toISOString(),
      };
      nextTags = [...tags, saved];
    }
    await setDoc(ref, { tags: nextTags, updatedAt: serverTimestamp() }, { merge: true });
    return serializeTag(saved, saved.order || 0);
  } catch (error) {
    console.error("[CK-9082] Error saving clinic tag:", {
      errorCode: "CK-9082",
      errorMessage: error.message,
      tagId: id || null,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a tag from the registry and strip it from every clinic that has it.
 */
export const deleteNivClinicTag = async (tagId) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.SETTINGS, CLINIC_TAGS_DOC_ID);
    const snap = await getDoc(ref);
    const tags = snap.exists() && Array.isArray(snap.data().tags) ? snap.data().tags : [];
    await setDoc(
      ref,
      { tags: tags.filter((t) => t.id !== tagId), updatedAt: serverTimestamp() },
      { merge: true }
    );
    const usersSnap = await getDocs(
      query(collection(db, NIV_COLLECTIONS.CLINIC_CRM), where("tagIds", "array-contains", tagId))
    );
    const ids = usersSnap.docs.map((d) => d.id);
    for (let i = 0; i < ids.length; i += BATCH_CHUNK) {
      const batch = writeBatch(db);
      ids.slice(i, i + BATCH_CHUNK).forEach((clinicId) => {
        batch.update(doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId), {
          tagIds: arrayRemove(tagId),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
    }
    return { tagId, removedFrom: ids.length };
  } catch (error) {
    console.error("[CK-9083] Error deleting clinic tag:", {
      errorCode: "CK-9083",
      errorMessage: error.message,
      tagId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Replace a clinic's tag assignment (full array — deterministic for the
 * optimistic cache patch).
 */
export const setNivClinicTagIds = async (clinicId, tagIds) => {
  try {
    const clean = Array.from(new Set((tagIds || []).filter(Boolean)));
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      { clinicId, tagIds: clean, updatedAt: serverTimestamp() },
      { merge: true }
    );
    return { clinicId, tagIds: clean };
  } catch (error) {
    console.error("[CK-9084] Error setting clinic tags:", {
      errorCode: "CK-9084",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

export const CONTACT_CHANNELS = ["call", "whatsapp", "sms", "email", "other"];

/**
 * Log a contact with the clinic owner. The caller supplies the id so the
 * optimistic entry is identical to the persisted one. Clears any pending
 * "needs contact" dismissal — a real contact supersedes it.
 */
export const addNivCrmContact = async (clinicId, { id, date, channel, note, author }) => {
  try {
    const contact = {
      id: id || generateId(),
      date: date || new Date().toISOString(),
      channel: CONTACT_CHANNELS.includes(channel) ? channel : "other",
      note: (note || "").trim() || null,
      author: author || null,
      createdAt: new Date().toISOString(),
    };
    const ref = doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId);
    const snap = await getDoc(ref);
    const existingLast = snap.exists() ? snap.data().lastContactAt : null;
    const existingLastIso = existingLast
      ? serializeForRedux({ date: existingLast }).date
      : null;
    const lastContactAt =
      existingLastIso && new Date(existingLastIso) > new Date(contact.date)
        ? existingLastIso
        : contact.date;
    await setDoc(
      ref,
      {
        clinicId,
        contacts: arrayUnion(contact),
        lastContactAt,
        contactDismissedKey: null,
        contactDismissedAt: null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { clinicId, contact, lastContactAt };
  } catch (error) {
    console.error("[CK-9085] Error adding CRM contact:", {
      errorCode: "CK-9085",
      errorMessage: error.message,
      clinicId,
      channel,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Remove a contact entry and recompute lastContactAt from what remains.
 */
export const deleteNivCrmContact = async (clinicId, contactId) => {
  try {
    const ref = doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { clinicId, contactId, lastContactAt: null };
    const contacts = (snap.data().contacts || []).filter((c) => c.id !== contactId);
    const lastContactAt = contacts.reduce((max, c) => {
      const iso = c.date ? serializeForRedux({ date: c.date }).date : null;
      if (!iso) return max;
      return !max || new Date(iso) > new Date(max) ? iso : max;
    }, null);
    await updateDoc(ref, { contacts, lastContactAt, updatedAt: serverTimestamp() });
    return { clinicId, contactId, lastContactAt };
  } catch (error) {
    console.error("[CK-9086] Error deleting CRM contact:", {
      errorCode: "CK-9086",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Dismiss the current "needs contact" trigger for a clinic. The key encodes
 * the trigger reason + anchor (e.g. "trialEnding:<trialEndDate>") so a new
 * trigger re-surfaces the clinic automatically.
 */
export const dismissNivContactNeed = async (clinicId, dismissKey) => {
  try {
    const contactDismissedAt = new Date().toISOString();
    await setDoc(
      doc(db, NIV_COLLECTIONS.CLINIC_CRM, clinicId),
      {
        clinicId,
        contactDismissedKey: dismissKey || null,
        contactDismissedAt,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { clinicId, contactDismissedKey: dismissKey || null, contactDismissedAt };
  } catch (error) {
    console.error("[CK-9087] Error dismissing contact need:", {
      errorCode: "CK-9087",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Activation counts for the funnel: appointments + completed appointments
 * per clinic via count() aggregates (≈2 reads per clinic, no doc payload).
 * Per-clinic failures yield nulls (unknown), never fake zeros.
 * Returns { [clinicId]: { appointments, completed } }.
 */
export const getClinicsActivationCounts = async (clinicIds = []) => {
  try {
    const result = {};
    const CHUNK = 20;
    for (let i = 0; i < clinicIds.length; i += CHUNK) {
      const chunk = clinicIds.slice(i, i + CHUNK);
      const rows = await Promise.all(
        chunk.map(async (clinicId) => {
          try {
            const apptsRef = collection(db, "clinics", clinicId, "appointments");
            const [allSnap, completedSnap] = await Promise.all([
              getCountFromServer(apptsRef),
              getCountFromServer(query(apptsRef, where("status", "==", "completed"))),
            ]);
            return {
              clinicId,
              appointments: allSnap.data().count,
              completed: completedSnap.data().count,
            };
          } catch {
            return { clinicId, appointments: null, completed: null };
          }
        })
      );
      rows.forEach((r) => {
        result[r.clinicId] = { appointments: r.appointments, completed: r.completed };
      });
    }
    return result;
  } catch (error) {
    console.error("[CK-9088] Error fetching clinics activation counts:", {
      errorCode: "CK-9088",
      errorMessage: error.message,
      clinicCount: clinicIds.length,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Delete a prospect for good: the prospect doc, its whitelist entry and every
 * linked call request. getNivProspectsBoard re-materializes prospects from
 * call requests + whitelist on every load, so leaving those behind made the
 * lead reappear right after "deleting" it.
 * Does NOT touch clinics/therapists.
 */
export const deleteProspect = async ({ phone, callRequestIds = [] }) => {
  try {
    const normalized = normalizePhoneToInternational(phone) || phone;
    const local = normalized.startsWith("+972") ? `0${normalized.slice(4)}` : null;

    // Call requests store the phone as typed — find every stored variant.
    const variants = [normalized, normalized.replace(/^\+/, "")];
    if (local) variants.push(local);
    const byPhoneSnap = await getDocs(
      query(collection(db, NIV_COLLECTIONS.CALL_REQUESTS), where("phone", "in", variants))
    );
    const requestIds = new Set(callRequestIds || []);
    byPhoneSnap.forEach((d) => requestIds.add(d.id));

    const batch = writeBatch(db);
    batch.delete(doc(db, NIV_COLLECTIONS.PROSPECTS, normalized));
    batch.delete(doc(db, NIV_COLLECTIONS.REGISTRATION_WHITELIST, normalized));
    requestIds.forEach((id) => batch.delete(doc(db, NIV_COLLECTIONS.CALL_REQUESTS, id)));
    await batch.commit();

    return { id: normalized, deleted: true, deletedCallRequests: requestIds.size };
  } catch (error) {
    console.error("[CK-9063] Error deleting prospect:", {
      errorCode: "CK-9063",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// TELEMETRY & SCORES (new analytics pipeline)
// Error codes: CK-9070 - CK-9079
// ============================================

const getIsraelDateKeyNDaysAgo = (days) => {
  const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

/**
 * System-wide daily snapshots (niv_system_daily) for the NiV "התקדמות"
 * progress charts — one numbers-only doc per day written by the nightly
 * telemetry function (usage deltas, entity counts, business KPIs).
 */
export const getSystemDailySeries = async ({ days = 30 } = {}) => {
  try {
    const startKey = getIsraelDateKeyNDaysAgo(days);
    const snapshot = await getDocs(
      query(
        collection(db, NIV_COLLECTIONS.SYSTEM_DAILY),
        where("dateKey", ">=", startKey),
        orderBy("dateKey", "asc")
      )
    );
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        dateKey: data.dateKey || docSnap.id,
        monthKey: data.monthKey || null,
        daily: data.daily || {},
        counts: data.counts || {},
        business: data.business || {},
        countErrors: data.countErrors || 0,
        clinicsScanned: data.clinicsScanned || 0,
      };
    });
  } catch (error) {
    console.error("[CK-9076] Error fetching system daily series:", {
      errorCode: "CK-9076",
      errorMessage: error.message,
      days,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Global telemetry overview for the NiV dashboard.
 * Reads clinic daily roll-up docs for the last N days and aggregates in JS.
 */
export const getTelemetryOverview = async ({ days = 30 } = {}) => {
  try {
    const startKey = getIsraelDateKeyNDaysAgo(days);
    const snapshot = await getDocs(
      query(
        collection(db, NIV_COLLECTIONS.TELEMETRY_DAILY),
        where("dateKey", ">=", startKey)
      )
    );

    const dailySeries = {}; // dateKey -> { activeClinics, activeTherapists, sessions }
    const deviceTotals = {};
    const pageTotals = {};
    const featureTotals = {};
    const pwaInstallTotals = {}; // banner action -> count
    const perClinic = {}; // clinicId -> rollup
    let totalSessions = 0;
    let pwaSessions = 0;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const day = dailySeries[data.dateKey] || {
        dateKey: data.dateKey,
        activeClinics: 0,
        activeTherapists: 0,
        sessions: 0,
      };
      day.activeClinics += 1;
      day.activeTherapists += data.activeTherapistCount || 0;
      day.sessions += data.sessions || 0;
      dailySeries[data.dateKey] = day;

      totalSessions += data.sessions || 0;
      pwaSessions += data.pwaStandaloneSessions || 0;
      for (const [k, v] of Object.entries(data.sessionsByDevice || {})) {
        deviceTotals[k] = (deviceTotals[k] || 0) + v;
      }
      for (const [k, v] of Object.entries(data.pageViews || {})) {
        pageTotals[k] = (pageTotals[k] || 0) + v;
      }
      for (const [k, v] of Object.entries(data.features || {})) {
        featureTotals[k] = (featureTotals[k] || 0) + v;
      }
      for (const [k, v] of Object.entries(data.pwaInstallPrompt || {})) {
        pwaInstallTotals[k] = (pwaInstallTotals[k] || 0) + v;
      }

      const clinicAgg = perClinic[data.clinicId] || {
        clinicId: data.clinicId,
        clinicName: data.clinicName || "",
        sessions: 0,
        activeDays: 0,
        features: {},
      };
      clinicAgg.sessions += data.sessions || 0;
      clinicAgg.activeDays += 1;
      for (const [k, v] of Object.entries(data.features || {})) {
        clinicAgg.features[k] = (clinicAgg.features[k] || 0) + v;
      }
      perClinic[data.clinicId] = clinicAgg;
    });

    return {
      days,
      startKey,
      dailySeries: Object.values(dailySeries).sort((a, b) =>
        a.dateKey.localeCompare(b.dateKey)
      ),
      deviceTotals,
      pageTotals,
      featureTotals,
      pwaInstallTotals,
      totalSessions,
      pwaSessions,
      perClinic: Object.values(perClinic),
    };
  } catch (error) {
    console.error("[CK-9070] Error fetching telemetry overview:", {
      errorCode: "CK-9070",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * All clinic usage docs (one small doc per clinic) — logins, integrations and
 * meeting counts. Feeds the NiV clinics list columns and sorting.
 */
export const getClinicUsageList = async () => {
  try {
    const snapshot = await getDocs(
      collection(db, NIV_COLLECTIONS.CLINIC_USAGE)
    );
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    console.error("[CK-9071] Error fetching clinic usage list:", {
      errorCode: "CK-9071",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Per-clinic telemetry drill-down: the usage doc + daily docs (clinic-level
 * and per-therapist) for the last N days.
 */
export const getClinicTelemetryDetail = async (clinicId, { days = 90 } = {}) => {
  try {
    const startKey = getIsraelDateKeyNDaysAgo(days);

    const [usageSnap, clinicDailySnap, therapistDailySnap] =
      await Promise.all([
        getDoc(doc(db, NIV_COLLECTIONS.CLINIC_USAGE, clinicId)),
        getDocs(
          query(
            collection(db, NIV_COLLECTIONS.TELEMETRY_DAILY),
            where("clinicId", "==", clinicId),
            where("dateKey", ">=", startKey)
          )
        ),
        getDocs(
          query(
            collection(db, NIV_COLLECTIONS.TELEMETRY_DAILY_THERAPIST),
            where("clinicId", "==", clinicId),
            where("dateKey", ">=", startKey)
          )
        ),
      ]);

    return {
      clinicId,
      usage: usageSnap.exists() ? usageSnap.data() : null,
      clinicDaily: clinicDailySnap.docs
        .map((d) => d.data())
        .sort((a, b) => a.dateKey.localeCompare(b.dateKey)),
      therapistDaily: therapistDailySnap.docs
        .map((d) => d.data())
        .sort((a, b) => a.dateKey.localeCompare(b.dateKey)),
    };
  } catch (error) {
    console.error("[CK-9072] Error fetching clinic telemetry detail:", {
      errorCode: "CK-9072",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Feature-tips doc for a clinic (nightly-computed catalog + `visible` map).
 * Therapists only see tips whose visible[featureKey] === true.
 */
export const getClinicFeatureTips = async (clinicId) => {
  try {
    const [tipsSnap, nudgesSnap] = await Promise.all([
      getDoc(doc(db, "clinics", clinicId, "insights", "featureTips")),
      getDoc(doc(db, "clinics", clinicId, "insights", "featureNudges")),
    ]);
    if (!tipsSnap.exists()) return null;
    return {
      ...tipsSnap.data(),
      nudges: nudgesSnap.exists() ? nudgesSnap.data() : null,
    };
  } catch (error) {
    console.error("[CK-9073] Error fetching clinic feature tips:", {
      errorCode: "CK-9073",
      errorMessage: error.message,
      clinicId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Override whether a specific feature tip is shown to the clinic's therapists.
 * Tri-state: true = force-show, false = force-hide, null = back to automatic
 * (the nightly nudge engine decides; on clinics from before that engine, no
 * override means the tip simply never shows).
 * Writes only the `visible` map — the nightly scoring rewrite of `tips`
 * uses merge:true so this survives recomputes.
 */
export const setClinicFeatureTipVisibility = async (
  clinicId,
  featureKey,
  visible
) => {
  try {
    await setDoc(
      doc(db, "clinics", clinicId, "insights", "featureTips"),
      {
        visible: {
          [featureKey]: visible === null ? deleteField() : visible === true,
        },
      },
      { merge: true }
    );
    return { success: true };
  } catch (error) {
    console.error("[CK-9074] Error setting feature tip visibility:", {
      errorCode: "CK-9074",
      errorMessage: error.message,
      clinicId,
      featureKey,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// ============================================
// FOOD PROMOTION REQUESTS (nutrition clinics)
// ============================================
// A coach adding a food to their own clinic catalog raises a request here.
// NiV reviews it and, when it is good, copies it into the global `foods`
// collection so every nutrition clinic gets it.
//
// Approval runs the payload through the SAME normalizer the seed import and
// the coach-facing form use — a request created before a contract change can
// never slip a malformed food into the shared catalog.
// ============================================

/**
 * Pending (and recently handled) promotion requests, newest first.
 *
 * Error Code: CK-8610
 */
export const getFoodPromotionRequests = async (status = "pending", limitCount = 200) => {
  try {
    const requestsRef = collection(db, "foodPromotionRequests");
    const constraints = [];
    if (status) constraints.push(where("status", "==", status));
    constraints.push(orderBy("createdAt", "desc"), limit(limitCount));

    const snapshot = await getDocs(query(requestsRef, ...constraints));

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : null,
        reviewedAt: data.reviewedAt?.toDate
          ? data.reviewedAt.toDate().toISOString()
          : null,
      };
    });
  } catch (error) {
    console.error("[CK-8610] Error fetching food promotion requests:", {
      errorCode: "CK-8610",
      errorMessage: error.message,
      status,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Approve a request: normalize the food, write it to the global catalog under
 * its name slug (merge, so an approved-twice food updates rather than
 * duplicates), and mark the request handled.
 *
 * @param {string} requestId
 * @param {object} [overrides] - NiV's edits to the submitted food, if any
 *
 * Error Code: CK-8611
 */
export const approveFoodPromotionRequest = async (requestId, overrides = {}) => {
  try {
    const requestRef = doc(db, "foodPromotionRequests", requestId);
    const requestSnap = await getDoc(requestRef);
    if (!requestSnap.exists()) throw new Error("הבקשה לא נמצאה");

    const request = requestSnap.data();
    const result = normalizeFood(
      { ...request.food, ...overrides },
      { source: "global" }
    );
    if (!result.ok) {
      const error = new Error(result.errors.join("; "));
      error.validationErrors = result.errors;
      throw error;
    }

    await setDoc(
      doc(db, "foods", result.id),
      { ...result.food, updatedAt: serverTimestamp(), createdAt: serverTimestamp() },
      { merge: true }
    );

    await updateDoc(requestRef, {
      status: "approved",
      approvedFoodId: result.id,
      reviewedAt: serverTimestamp(),
    });

    return { success: true, foodId: result.id, foodName: result.food.foodName };
  } catch (error) {
    console.error("[CK-8611] Error approving food promotion request:", {
      errorCode: "CK-8611",
      errorMessage: error.message,
      requestId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Reject a request. The clinic keeps its own copy of the food either way —
 * rejection only means it does not join the shared catalog.
 *
 * Error Code: CK-8612
 */
export const rejectFoodPromotionRequest = async (requestId, reason = "") => {
  try {
    await updateDoc(doc(db, "foodPromotionRequests", requestId), {
      status: "rejected",
      rejectionReason: reason || null,
      reviewedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("[CK-8612] Error rejecting food promotion request:", {
      errorCode: "CK-8612",
      errorMessage: error.message,
      requestId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};
